<template>
  <div class="dij-diagrambuilder">
    <b-loading
      :is-full-page="true"
      :active.sync="isLoadingRatBubble"
      :can-cancel="false"
    ></b-loading>
    <input
      ref="diagramChooser"
      type="file"
      class="hidden"
      accept="image/*;capture=camera"
      @change="importDiagram"
    />
    <div class="dij-diagrambuilder-canvas-container">
      <Toolbar
        :showToolbar="showToolbar"
        :mode="mode"
        :isImageSelected="isImageSelected"
        :readOnly="readOnly"
        @enableMode="enableMode"
      />

      <div class="dij-diagrambuilder-zoomoption">
        <b-icon
          class="dij-diagrambuilder-zoom-icon"
          :class="isImageSelected ? '' : 'disabled'"
          icon="home"
          @click.native="zoomDefault"
        />
        <b-icon
          class="dij-diagrambuilder-zoom-icon"
          :class="isImageSelected ? '' : 'disabled'"
          icon="magnify-plus-outline"
          @click.native="zoomIn"
        />
        <b-icon
          class="dij-diagrambuilder-zoom-icon"
          :class="isImageSelected ? '' : 'disabled'"
          icon="magnify-minus-outline"
          @click.native="zoomOut"
        />
        <template>
          <div
            :title="$t('RAT')"
            class="dij-diagrambuilder-toolbar-rat"
            :class="{
              disabled: !isImageSelected || readOnly,
              'dij-tool-active': mode === 'rat',
            }"
            @click="enableRATMode"
          >
            <b-icon
              icon="hammer-wrench"
              size="is-medium"
              custom-class="no-hover"
            />
          </div>
        </template>

        <template v-if="tool === 'inspector'">
          <div
            :title="$t('freefinding')"
            class="dij-diagrambuilder-toolbar-freefinding"
            :class="{
              disabled: !isImageSelected || readOnly,
              'dij-tool-active': mode === 'freefinding',
            }"
            @click="enableFreefindingMode"
          >
            <b-icon
              :icon="
                freeFindingTool
                  ? 'clipboard-text-search-outline'
                  : 'clipboard-text-search'
              "
              size="is-medium"
            ></b-icon>
          </div>
        </template>
      </div>

      <div
        class="dij-diagrambuilder-zoomoption"
        :class="showList ? 'bubble-zoom' : 'bubble-zoom-at-margin'"
      >
        <img
          class="dij-diagrambuilder-zoom-image"
          :class="isImageSelected ? '' : 'disabled'"
          src="/img/icons/bubble-center.svg"
          @click="zoomDefaultBubble"
        />

        <img
          class="dij-diagrambuilder-zoom-image"
          :class="isImageSelected ? '' : 'disabled'"
          src="/img/icons/bubble-plus.svg"
          @click="zoomInBubble"
        />

        <img
          class="dij-diagrambuilder-zoom-image"
          :class="isImageSelected ? '' : 'disabled'"
          src="/img/icons/bubble-minus.svg"
          @click="zoomOutBubble"
        />
      </div>

      <div
        class="dij-diagrambuilder-canvas"
        :id="'dij-diagrambuilder-canvas-' + task._id"
      >
        <canvas ref="canvas" :id="'canvas-diagram-' + task._id" />
        <div v-if="isImageSelected" class="dij-diagrambuild-job-info">
          <div>
            {{ $t('document_number') }}: {{ task.documentNumber }}
            {{ $t('revision') }}:
            {{ task.revision }}
          </div>
        </div>
        <div v-else-if="!showToolbar" class="dij-diagrambuilder-upload">
          {{ $t('diagram_builder.empty_diagram') }}
        </div>
        <div v-else class="dij-diagrambuilder-upload">
          <b-button
            icon-left="paperclip"
            :disabled="readOnly"
            @click="chooseDiagramToImport"
            >{{ $t('diagram_builder.import_diagram') }}</b-button
          >
        </div>
        <div
          class="dij-table-measurments"
          v-if="tableBubblesItems.length > 0 && !reportMode"
        >
          <b-collapse
            class="panel"
            animation="slide"
            @open="() => (isOpenTableMeasurments = true)"
            @close="() => (isOpenTableMeasurments = false)"
            aria-id="contentIdForTable"
          >
            <template #trigger>
              <div
                class="panel-heading table-title"
                style="width: 120px"
                role="button"
                aria-controls="contentIdForTable"
                :aria-expanded="false"
              >
                <strong
                  >{{ $t('table') }}
                  <b-icon icon="menu-down" v-if="isOpenTableMeasurments" />
                  <b-icon icon="menu-up" v-else />
                </strong>
              </div>
            </template>
            <table-bubble-measurments
              :tableBubblesItems="tableBubblesItems"
              :tool="tool"
              :readOnly="readOnly"
              ref="tableBubbles"
            />
          </b-collapse>
        </div>
      </div>

      <div id="arrow-circle-snap-helper" v-if="showArrowCircleSnapHelper">
        <div class="arrow-line-snap" style="transform: rotate(0deg)"></div>
        <div class="arrow-line-snap" style="transform: rotate(45deg)"></div>
        <div class="arrow-line-snap" style="transform: rotate(90deg)"></div>
        <div class="arrow-line-snap" style="transform: rotate(135deg)"></div>
        <div class="arrow-line-snap" style="transform: rotate(180deg)"></div>
        <div class="arrow-line-snap" style="transform: rotate(225deg)"></div>
        <div class="arrow-line-snap" style="transform: rotate(270deg)"></div>
        <div class="arrow-line-snap" style="transform: rotate(315deg)"></div>
      </div>

      <inspection-list
        v-if="showList"
        ref="list"
        v-model="inspectionListItems"
        :tool="tool"
        :readOnly="readOnly"
      />

      <div
        class="dij-diagrambuilder-inspection-detail"
        v-if="isItemDetailVisible && selectedItem.type !== 'rat'"
      >
        <header class="modal-card-head">
          {{ $t(selectedItem.type) }}
        </header>
        <section class="modal-card-body dij-inspection-detail-body">
          <inspection-detail
            v-model="selectedItem"
            :readOnly="
              readOnly || isLoadingBubbleUpdates || processingNewBubble
            "
          />
        </section>
        <footer class="modal-card-foot">
          <b-button
            v-if="isMoveMeasurementVisible"
            @click="enableMoveMeasurementMode"
            :disabled="readOnly"
            :type="explicitMoveMeasurementMode ? 'is-primary' : 'is-normal'"
            ><b-icon icon="arrow-all"
          /></b-button>
          <b-button
            type="is-danger"
            :disabled="readOnly || processingNewBubble"
            :title="
              processingNewBubble ? $t('diagram_builder.bubble_processing') : ''
            "
            @click="deleteInspection"
            :loading="
              processingNewBubble || isLoadingBubbleUpdates || isLoading
            "
          >
            {{ $t('delete') }}
          </b-button>
          <b-button type="is-primary" @click="closeInspection">
            {{ $t('close') }}
          </b-button>
        </footer>
      </div>

      <div class="dij-diagrambuilder-inspection-rat" v-if="isItemRATVisible">
        <b-modal
          v-if="isCameraVisible"
          :active="true"
          class="dij-ra-camera-modal"
          full-screen
        >
          <camera @cancel="hideCamera" @ok="processCamera" />
        </b-modal>
        <b-modal
          v-if="isAnnotatorVisible"
          :active="true"
          class="dij-ra-annotator-modal"
        >
          <image-annotator
            :image="imageToBeAnnotated"
            @cancel="hideAnnotator"
            @ok="processAnnotator"
            @okandnew="newAnnotator"
            :nextCamera="annotatorCameraNext"
          />
        </b-modal>
        <image-gallery
          class="dij-ra-image-gallery"
          v-if="isGalleryVisible"
          v-model="selectedItem.images"
          @close="hideGallery"
        />
        <header class="modal-card-head">
          {{ $t(selectedItem.type.toUpperCase()) }}
        </header>
        <section class="modal-card-body">
          <RatInspectionDetail
            v-model="selectedItem"
            :tool="tool"
            ref="refRatDetail"
          />
        </section>
        <footer class="modal-card-foot">
          <b-button
            type="is-danger"
            @click="deleteInspection"
            :disabled="readOnly"
            :loading="isLoadingRatInspection"
          >
            {{ $t('delete') }}
          </b-button>
          <div
            v-if="!selectedItem.isMinor"
            style="display: flex; gap: 10px; height: 36px"
          >
            <b-dropdown
              :expanded="true"
              :close-on-click="false"
              :disabled="readOnly"
              ref="fileUploadDropdown"
              position="is-top-left"
            >
              <template #trigger>
                <b-icon
                  icon="camera"
                  size="is-medium"
                  style="margin-top: -16px"
                />
              </template>

              <b-dropdown-item class="custom-dropdown-item">
                <div class="dij-diagrambuilder-file-chooser">
                  <label class="file-upload">
                    <input
                      class="file-input"
                      ref="imageUpload"
                      type="file"
                      multiple
                      :disabled="readOnly"
                      accept="image/*;capture=camera"
                      @change="handleFileChooserChange($event)"
                    />
                    <span class="mdi mdi-folder mdi-48px">{{
                      $t('diagram_builder.choose_files')
                    }}</span>
                  </label>
                </div>
              </b-dropdown-item>
              <b-dropdown-item
                class="custom-dropdown-item"
                @click.native="showCamera()"
                :disabled="readOnly"
              >
                <div class="dij-diagrambuilder-file-chooser">
                  <b-icon icon="camera" size="is-large" />
                  {{ $t('diagram_builder.take_photo') }}
                </div>
              </b-dropdown-item>
            </b-dropdown>
            <b-icon
              icon="image"
              size="is-medium"
              :class="
                getImagesRAT(selectedItem) > 0
                  ? 'dij-ra-gallery-button'
                  : 'dij-ra-gallery-button disabled'
              "
              @click.native="showGallery(selectedItem)"
            />

            <b-tag
              rounded
              type="is-info"
              @click.native="showGallery(selectedItem)"
              v-if="getImagesRAT(selectedItem) > 0"
            >
              {{ getImagesRAT(selectedItem) }}</b-tag
            >
          </div>
          <b-button type="is-primary" @click="saveInspectionRat">
            {{ $t('close') }}
          </b-button>
        </footer>
      </div>

      <div
        class="dij-diagrambuilder-inspection-tooltip"
        v-if="isItemToolTipVisible && toolTipInspection.type !== 'rat'"
      >
        <header class="modal-card-head">
          {{ toolTipInspection.number }}. {{ $t(toolTipInspection.type) }}
        </header>
        <section class="modal-card-body dij-inspection-detail-body">
          <inspection-tooltip v-model="toolTipInspection" />
        </section>
      </div>

      <div v-for="posImg in positionImages">
        <img :src="posImg.src" :id="posImg.id" />
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';

// eslint-disable-next-line import/no-extraneous-dependencies
import { getDocument } from 'pdfjs-dist';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'pdfjs-dist/webpack.mjs';
import Tiff from 'tiff';
import * as paper from 'paper';
import * as Hammer from 'hammerjs';
import { v4 } from 'uuid';
import InspectionDetail from './InspectionDetail';
import InspectionList from './InspectionList';
import RatInspectionDetail from './RatInspectionDetail';
import InspectionTooltip from './InspectionTooltip';
import Toolbar from './Toolbar';

import DIJVueConfigurations from '../../helpers/DIJVueConfigurations';
import client from '../../config/api';
import {
  DELETE_BUBBLE,
  CREATE_BUBBLE,
  UPDATE_BUBBLE,
  SAVE_BUBBLES,
  GET_BUBBLES,
} from '../../store/bubbles/actions/actionTypes';
import {
  ADD_REPAIR_ASSESSMENT_ROW,
  DELETE_REPAIR_ASSESSMENT_ROW,
  GET_JOB,
  UPDATE_REPAIR_ASSESSMENT_ROW,
} from '../../store/jobs/actions/actionTypes';
import { GET_CURRENT_JOB } from '../../store/jobs/getters/getterTypes';
import { SET_CURRENT_TASK_DIAGRAM_SOURCE } from '../../store/tasks/mutations/mutationTypes';
import { SAVE_CURRENT_TASK_STATE } from '../../store/tasks/actions/actionTypes';
import { GET_LOADING_STATE } from '../../store/loading/getters/getterTypes';
import KeyHandlerMixin from '../../mixins/KeyHandlerMixin';
import TableBubbleMeasurments from './TableBubbleMeasurments';
import BubbleDTO from '../../shared/dtos/BubbleDTO';
import imageHelper from '../../helpers/ImageHelper';
import { GET_SYNCHRONIZED_BUBBLES } from '../../store/bubbles/getters/getterTypes';
import { REMOVE_SYNCHRONIZED_BUBBLE } from '../../store/bubbles/mutations/mutationTypes';
import {
  ENTITY_TYPES as CONFLICT_ENTITY_TYPES,
  setSnapshot as setConflictSnapshot,
} from '../../helpers/conflictTracker';
import OfflineMixin from '../../mixins/OfflineMixin';
import Camera from '../layout/Camera';
import ImageAnnotator from '../layout/ImageAnnotator';
import ImageGallery from '../layout/ImageGallery';
import {
  WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION,
  WEBSOCKET_EVENT_UNSUBSCRIBE_FROM_ACTION,
} from '../../store/websocket/actions/actionTypes';

const uuidv4 = v4;

const EMPTY_MEASUREMENT_VALUE = {
  value: '',
  images: [],
  comment: '',
};

const EMPTY_VISUAL_MEASUREMENT_VALUE = {
  value: 'notCompleted',
  images: [],
  comment: '',
};

const MAKERS_COLORS = {
  default: '#000', // #808080
};

export default {
  name: 'diagram-builder',
  components: {
    InspectionList,
    InspectionDetail,
    RatInspectionDetail,
    Toolbar,
    TableBubbleMeasurments,
    Camera,
    ImageAnnotator,
    ImageGallery,
    InspectionTooltip,
  },
  mixins: [KeyHandlerMixin, OfflineMixin],
  props: {
    taskProp: Object,
    tool: String,
    showList: {
      type: Boolean,
      default: true,
    },
    showToolbar: {
      type: Boolean,
      default: true,
    },
    reportMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    customerOutOfToleranceColors: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['cancel', 'ok'],
  watch: {
    isItemDetailVisible(isVisible) {
      if (isVisible) {
        this.isItemToolTipVisible = false;
      }
      this.setKeyHandlerCallback(
        'cancel',
        isVisible ? this.closeInspection : null
      );
    },
    isItemRATVisible(isVisible) {
      if (isVisible) {
        this.isItemToolTipVisible = false;
      }
      this.setKeyHandlerCallback(
        'cancel',
        isVisible ? this.closeInspection : null
      );
    },
    isCustomerReport() {
      this.initializeMarkers();
    },

    customerOutOfToleranceColors() {
      this.initializeMarkers();
    },
    selectedItem(newItem, old) {
      if (old._id && old._id !== newItem._id) {
        const isBubbleStillValid = this.inspections.find(
          (bubble) => bubble._id === old._id
        );
        if (isBubbleStillValid) {
          // if (old.hasChanges) this.handleBubbleUpdate(old);
          this.handleBubbleUpdate(old);
        }
      }
    },
    isTableModeActive(newVal) {
      const elements = document.querySelectorAll(
        '.dij-diagrambuilder-toolbar-table'
      );
      if (newVal) {
        elements[0].style.backgroundColor = '#167df0';
      } else {
        elements[0].style.backgroundColor = '';
      }
    },
    synchronizedBubbles: {
      handler(bubbles) {
        bubbles.forEach((syncBubble) => {
          this.wsBubbleUpdated(syncBubble);
          this.$store.commit(REMOVE_SYNCHRONIZED_BUBBLE, syncBubble._id);
        });
      },
      deep: true,
      immediate: true,
    },
  },

  computed: {
    task() {
      return this.taskProp;
    },
    job() {
      return this.$store.getters[GET_CURRENT_JOB];
    },
    user() {
      return this.$store.state.currentUser;
    },
    synchronizedBubbles() {
      return this.$store.getters[GET_SYNCHRONIZED_BUBBLES];
    },
    isLoading() {
      return this.$store.getters[GET_LOADING_STATE];
    },
    isCustomerReport() {
      if (this.customerOutOfToleranceColors) return false;
      return !this.$store.state.ReportsModule.isInternalReport;
    },
    isMoveMeasurementVisible() {
      return (
        this.selectedItem &&
        ['length', 'line', 'arrow'].includes(this.selectedItem.type)
      );
    },
    inspectionListItems() {
      return this.inspections.filter(
        (inspection) =>
          inspection.type !== 'rat' && inspection.type !== 'position-image'
      );
    },
    tableBubblesItems() {
      return this.inspections.filter(
        (inspection) =>
          inspection.inTable &&
          inspection.type !== 'rat' &&
          inspection.type !== 'position-image'
      );
    },
    isTaskCompleted() {
      return this.task.workStatusCode
        ? this.task.workStatusCode.toString() === '2' || this.job.jobCompleted
        : false;
    },

    showArrowCircleSnapHelper() {
      if (
        this.selectedItem &&
        ['arrow', 'line'].includes(this.selectedItem.type) &&
        this.shiftPressed
      ) {
        return true;
      }

      if (this.shiftPressed && this.mode === 'arrow' && this.arrowStage === 1)
        return true;

      if (this.shiftPressed && this.mode === 'line' && this.lineStage === 1)
        return true;

      return false;
    },
  },

  data() {
    return {
      processingNewBubble: false,
      bubbleWasMoved: false,
      scope: null,
      mode: 'select',
      inspections: [],
      annotations: [],
      isItemDetailVisible: false,
      isItemRATVisible: false,
      selectedItem: {},
      isImageSelected: false,
      explicitMoveMeasurementMode: false,
      image: null,
      selectTool: null,
      moveTool: null,
      diameterTool: null,
      runoutTool: null,
      linearLengthTool: null,
      alignedLengthTool: null,
      visualTool: null,
      genericTool: null,
      freeFindingTool: null,
      positionImageTool: null,
      positionImages: [],
      textTool: null,
      linkTool: null,
      ratTool: null,
      arrowTool: null,
      lineTool: null,
      isMouseButtonDown: false,
      isMovingImage: false,
      isMovingMarker: false,
      lengthStage: 0,
      lengthMode: 0,
      arrowStage: 0,
      lineStage: 0,
      multipleMeasurementSelector: null,
      switchBackTool: null,
      squareSizeBubble: 35,
      fontSizeBubble: 20,
      xDeltas: [],
      yDeltas: [],
      isLoadingRatInspection: false,
      resizeTimeout: null,
      enablePositionImageType: null,
      initialDragPoint: null,
      isZoomingBuubles: false,
      isTableModeActive: false,
      isOpenTableMeasurments: true,
      isLoadingRatBubble: false,
      isCameraVisible: false,
      isAnnotatorVisible: false,
      isGalleryVisible: false,
      annotatorCameraNext: false,
      isItemToolTipVisible: false,
      isLoadingBubbleUpdates: false,
      toolTipInspection: null,
      shiftPressed: false,
      arrowEndPoint: null,
      arrowCircleMousePosition: null,
    };
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);

    document.addEventListener('mousedown', this.checkClickOutsideCanvas);

    let imageSource;

    // Ensure `this.task` and `this.task.diagram` are valid
    if (
      !this.isNewTask() &&
      this.task?.diagram?.src &&
      typeof this.task.diagram.src === 'string'
    ) {
      // Extract path parts safely
      const parts = this.task.diagram.src.split('/');
      if (parts.length > 1) {
        const lastPart = parts.pop();
        const allButLast = parts.join('/');
        imageSource = `${allButLast}/${encodeURIComponent(lastPart)}`;
      } else {
        console.warn(`Invalid diagram source path: ${this.task.diagram.src}`);
      }

      // For local env, modify URL if valid
      if (imageSource && window.location.host.includes('localhost')) {
        imageSource = `http://localhost:4566/images${imageSource}`;
      }
    }

    // Ensure inspections and annotations are valid arrays
    this.inspections = Array.isArray(this.task?.diagram?.inspections)
      ? this.task.diagram.inspections.map((inspection) => ({
          ...inspection,
          measurements: inspection.measurements?.map((m) => ({ ...m })) || [],
        }))
      : [];

    this.annotations = Array.isArray(this.task?.diagram?.annotations)
      ? this.task.diagram.annotations
      : [];

    // Reset measurements for in-table inspections safely
    this.inspections.forEach((inspection) => {
      if (inspection?.inTable && Array.isArray(inspection.measurements)) {
        inspection.measurements = [];
      }
    });

    if (imageSource) {
      this.isImageSelected = true;
    } else {
      this.isImageSelected = false;
    }

    const canvas = document.querySelector(
      `#dij-diagrambuilder-canvas-${this.task._id}`
    );

    const zoomOptions = document.querySelector(
      '.dij-diagrambuilder-zoomoption'
    );

    if (!this.showToolbar && !this.showList) {
      canvas.style.width = '100%';
    } else if (!this.showToolbar) {
      canvas.style.width = 'calc(100% - 225px)';
    } else if (!this.showList) {
      zoomOptions.style.left = '70px';
      canvas.style.left = '70px';
      canvas.style.width = 'calc(100% - 70px)';
    } else {
      zoomOptions.style.left = '70px';
      canvas.style.left = '70px';
      canvas.style.width = 'calc(100% - 225px - 70px)';
    }
    canvas.style.width = `${canvas.clientWidth}px`;
    canvas.style.height = `${canvas.clientHeight}px`;

    this.scope = new paper.PaperScope();
    // Paper.js allows you to keep the line thin visually but make it easier to click by increasing the hit-test area.
    this.scope.settings.hitTolerance = 6;
    this.scope.setup(this.$refs.canvas);

    if (this.task.zoomLevel) {
      const { circleRadiusBubble, fontSizeBubble } = this.task.zoomLevel;
      this.squareSizeBubble = circleRadiusBubble;
      this.fontSizeBubble = fontSizeBubble;
    }

    let savedZoom = null;
    if (this.task.zoomLevel) {
      const { imageZoom } = this.task.zoomLevel;
      // this.scope.view.zoom = imageZoom;
      savedZoom = imageZoom;
    }
    if (this.isImageSelected) {
      const raster = new paper.Raster({
        source: imageSource,
        crossOrigin: 'anonymous', // ← this is key to prevent tainted canvas
      });

      this.image = raster;

      this.image.onLoad = () => {
        this.image.position = this.scope.view.center;
        this.initializeMarkers();
        if (savedZoom) {
          this.scope.view.zoom = savedZoom;
        } else {
          this.scope.view.zoom *= 0.75;
        }
      };
      this.image.onError = () => {
        this.initializeMarkers();
        if (savedZoom) {
          this.scope.view.zoom = savedZoom;
        } else {
          this.scope.view.zoom *= 0.75;
        }
      };
    }

    // Create the tools
    this.initializeSelectTool();
    this.initializeMoveTool();
    this.initializeDiameterTool();
    this.initializeRunoutTool();
    this.initializeLinearLengthTool();
    this.initializeAlignedLengthTool();
    this.initializeVisualTool();
    this.initializeGenericTool();
    this.initializeRATTool();
    this.initializeTextTool();
    this.initializeLinkTool();
    this.initializePositionImageTool();
    this.initializeArrowTool();
    this.initializeLineTool();
    this.initializeFreefindingTool();

    // Enable select tool
    this.enableSelectMode();

    let originalScale;
    let originalDistance;
    let lastPercentage = 1;

    const hammertime = new Hammer(this.$refs.canvas);
    // Enable pinch behavior support
    hammertime.get('pinch').set({ enable: true });

    // On start take the original distances and scale
    hammertime.on('pinchstart', (event) => {
      if (this.mode === 'select') {
        const dx = event.pointers[0].clientX - event.pointers[1].clientX;
        const dy = event.pointers[0].clientY - event.pointers[1].clientY;
        originalDistance = dx * dx + dy * dy;
        originalScale = lastPercentage;
      }
    });

    hammertime.on('pinch', (event) => {
      if (this.mode === 'select') {
        const dx = event.pointers[0].clientX - event.pointers[1].clientX;
        const dy = event.pointers[0].clientY - event.pointers[1].clientY;
        const distance = dx * dx + dy * dy;

        const scale =
          ((distance / originalDistance) * originalScale) / lastPercentage;

        const pinchCenter = new paper.Point(
          (event.pointers[0].clientX + event.pointers[1].clientX) / 2 -
            this.$refs.canvas.offsetLeft,
          (event.pointers[0].clientY + event.pointers[1].clientY) / 2 -
            this.$refs.canvas.offsetTop
        );

        this.scope.view.scale(scale, pinchCenter);

        lastPercentage = (distance / originalDistance) * originalScale;
      }
    });
  },
  created() {
    const wsMappings = {
      bubbleUpdated: this.wsBubbleUpdated,
      bubbleCreated: this.wsBubbleUpdated,
      bubbleDeleted: this.wsBubbleDeleted,
      bubblesCreated: this.wsBubblesCreated,
    };

    Object.keys(wsMappings).forEach(async (key) => {
      await this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
        action: key,
        callback: wsMappings[key],
      });
    });

    this.$root.$on('updateBubble', this.handleBubbleUpdate);
  },
  beforeDestroy() {
    const wsMappings = {
      bubbleUpdated: this.wsBubbleUpdated,
      bubbleCreated: this.wsBubbleUpdated,
      bubbleDeleted: this.wsBubbleDeleted,
      bubblesCreated: this.wsBubblesCreated,
    };

    Object.keys(wsMappings).forEach(async (key) => {
      await this.$store.dispatch(WEBSOCKET_EVENT_UNSUBSCRIBE_FROM_ACTION, {
        action: key,
        callback: wsMappings[key],
      });
    });

    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('mousedown', this.checkClickOutsideCanvas);

    this.$root.$off('updateBubble', this.handleBubbleUpdate);
  },
  methods: {
    onKeyDown(e) {
      if (e.key === 'Shift') this.shiftPressed = true;
    },
    onKeyUp(e) {
      if (e.key === 'Shift') this.shiftPressed = false;
    },

    getTargetList(type) {
      return ['text', 'arrow', 'line', 'link'].includes(type)
        ? this.annotations
        : this.inspections;
    },
    replaceItem(list, newItem) {
      const index = list.findIndex((item) => item._id === newItem._id);
      if (index !== -1) {
        list.splice(index, 1, newItem);
      } else {
        list.push(newItem);
      }
    },
    replaceItemProps(list, newItem, props, preferLocal = false) {
      const index = list.findIndex((i) => i._id === newItem._id);
      if (index === -1) {
        list.push(newItem);
        return;
      }

      if (!Array.isArray(props) || props.length === 0) {
        // Fields that must always reflect the server value, even when preferLocal is true
        const alwaysFromServer = ['version'];
        const serverOverrides = alwaysFromServer.reduce((acc, key) => {
          if (Object.prototype.hasOwnProperty.call(newItem, key)) {
            acc[key] = newItem[key];
          }
          return acc;
        }, {});
        const merged = preferLocal
          ? { ...newItem, ...list[index], ...serverOverrides }
          : { ...list[index], ...newItem };
        Vue.set(list, index, merged);
        return;
      }

      const existing = list[index];
      props.forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(newItem, k)) {
          Vue.set(existing, k, newItem[k]);
        }
      });
    },

    checkClickOutsideCanvas(e) {
      const canvas = document.getElementById(`canvas-diagram-${this.task._id}`);
      const rect = canvas.getBoundingClientRect();

      const isOutside =
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom;

      // Check if user clicks outside the canvas
      if (isOutside) {
        if (this.mode === 'arrow' && this.arrowStage === 1) {
          if (this.selectedItem && this.selectedItem.marker) {
            this.selectedItem.marker.remove();
            this.arrowStage = 0;
            this.selectedItem = null;
          }
        }

        if (this.mode === 'line' && this.lineStage === 1) {
          if (this.selectedItem && this.selectedItem.marker) {
            this.selectedItem.marker.remove();
            this.lineStage = 0;
            this.selectedItem = null;
          }
        }
      }
    },

    // This refreshes the page when the window is resized, such as when a table user switches from landscape to portrait.
    handleResize() {
      const userAgent = navigator.userAgent.toLowerCase();
      const isTablet = /ipad|android(?!.*mobile)|tablet/.test(userAgent);
      if (isTablet) {
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    },

    importAccuScanValues(data) {
      this.inspections.sort((a, b) => a.number - b.number);
      const runoutIndex = data.findIndex((runout) => runout[0] === 'Runout');
      const runoutAngle = data.findIndex(
        (runout) => runout[0] === 'Runout Max Angle'
      );
      let bubbleCount = 0;
      const invalidPositionRunout = [];
      const runoutCount = this.inspections.filter(
        (inspection) => inspection.type === 'runout'
      ).length;
      this.inspections.forEach((inspection) => {
        if (inspection.type === 'runout' && !inspection.multipleMeasurements) {
          for (
            let accuScanIndex = 1;
            accuScanIndex <= data[0].length - 1;
            accuScanIndex++
          ) {
            const matchReg =
              /^\s*(\d+)(?:\s*[-:|.,_)]+\s*(.*)|\s*([A-Za-z].*))?$/;
            const column = data[0][accuScanIndex].toString();
            const match = column.match(matchReg);
            if (match !== null && inspection.number === Number(match[1])) {
              if (data[runoutIndex][accuScanIndex] === undefined) {
                data[runoutIndex][accuScanIndex] = '';
              }
              if (data[runoutAngle][accuScanIndex] === undefined) {
                data[runoutAngle][accuScanIndex] = '';
              }

              inspection.measurements[0].value =
                data[runoutIndex][accuScanIndex];
              inspection.measurements[1].value =
                data[runoutAngle][accuScanIndex];
              this.makeSimpleMarker(inspection);
              bubbleCount++;
            }
          }
          inspection.isInspector = true;
        } else if (
          inspection.type === 'runout' &&
          inspection.multipleMeasurements
        ) {
          const firstColumnLength = data
            .slice(1) // remove header
            .filter((row) => row[0] != null && row[0] !== '').length;

          for (
            let accuScanIndex = 1;
            accuScanIndex <= data[0].length - 1;
            accuScanIndex++
          ) {
            const matchReg =
              /^\s*(\d+)(?:\s*[-:|.,_)]+\s*(.*)|\s*([A-Za-z].*))?$/;
            const column = data[0][accuScanIndex].toString();
            const match = column.match(matchReg);
            if (match !== null && inspection.number === Number(match[1])) {
              for (
                let runoutMaxAngle = 0;
                runoutMaxAngle < firstColumnLength;
                runoutMaxAngle++
              ) {
                const runoutAngleNew = data.findIndex(
                  (runout) => parseInt(runout[0], 10) === runoutMaxAngle + 1
                );
                if (runoutAngleNew === -1) {
                  return;
                }
              }

              if (firstColumnLength !== inspection.measurements.length) {
                invalidPositionRunout.push({
                  runout: inspection.number,
                  filePos: firstColumnLength,
                  actualPos: inspection.measurements.length,
                });
              }
              if (inspection.measurements.length === firstColumnLength) {
                for (
                  let runoutMaxAngle = 0;
                  runoutMaxAngle < firstColumnLength;
                  runoutMaxAngle++
                ) {
                  const runoutAngleNew = data.findIndex(
                    (runout) => parseInt(runout[0], 10) === runoutMaxAngle + 1
                  );

                  if (runoutAngleNew === -1) {
                    return;
                  }
                  if (data[runoutAngleNew][accuScanIndex] === undefined) {
                    data[runoutAngleNew][accuScanIndex] = '';
                  }

                  const newValue = data[runoutAngleNew][accuScanIndex].replace(
                    ',',
                    '.'
                  );
                  inspection.measurements[runoutMaxAngle].value = newValue;
                }

                bubbleCount++;
              }
              this.makeSimpleMarker(inspection);
            }
          }
          inspection.isInspector = true;
        }
      });
      this.$store.dispatch(SAVE_BUBBLES, {
        taskId: this.task._id,
        data: this.inspections,
      });
      if (invalidPositionRunout.length > 0) {
        this.$buefy.snackbar.open({
          message: `❌ Incorrect Position count (Runout):<br>${invalidPositionRunout
            .map(
              (r) =>
                `• Runout ${r.runout}: , Actual Pos=${r.actualPos},Pos in file=${r.filePos}`
            )
            .join('<br>')}`,
          type: 'is-danger',
          position: 'is-top',
          duration: 7000,
          pauseOnHover: true,
        });
      }

      if (
        runoutCount === data[0].length - 1 &&
        runoutCount > bubbleCount &&
        bubbleCount > 0
      ) {
        this.$buefy.snackbar.open({
          message: `AccuScan values imported successfully.<br> ${this.$t(
            'diagram_builder.note_runout_points_missing'
          )}<br> Note: not all DIJ Runout points were filled.`,
          type: 'is-warning',
          position: 'is-top',
          pauseOnHover: true,
          duration: 10000,
        });
      }

      if (
        runoutCount > bubbleCount &&
        data[0].length - 1 > runoutCount &&
        bubbleCount > 0
      ) {
        this.$buefy.snackbar.open({
          message: `AccuScan values imported successfully.<br> ${this.$t(
            'diagram_builder.note_runout_points_missing'
          )}<br> Note: not all DIJ Runout points were filled.`,
          type: 'is-warning',
          position: 'is-top',
          pauseOnHover: true,
          duration: 10000,
        });
      }

      if (
        runoutCount > data[0].length - 1 &&
        runoutCount > bubbleCount &&
        bubbleCount < data[0].length - 1
      ) {
        this.$buefy.snackbar.open({
          message: `AccuScan values imported successfully.<br> ${this.$t(
            'diagram_builder.note_runout_points_missing'
          )}<br> Note: not all DIJ Runout points were filled.`,
          type: 'is-warning',
          position: 'is-top',
          pauseOnHover: true,
          duration: 10000,
        });
      }

      if (
        runoutCount > data[0].length - 1 &&
        runoutCount > bubbleCount &&
        bubbleCount === data[0].length - 1
      ) {
        this.$buefy.snackbar.open({
          message:
            'AccuScan values imported successfully.<br> Note: not all DIJ Runout points were filled.',
          type: 'is-success',
          position: 'is-top',
          duration: 10000,
        });
      }

      if (runoutCount < data[0].length - 1 && runoutCount === bubbleCount) {
        this.$buefy.snackbar.open({
          message:
            'AccuScan values imported successfully.<br> Note: The diagram does not contain all of the runout points found in the file',
          type: 'is-success',
          position: 'is-top',
          duration: 10000,
        });
      } else if (
        runoutCount === data[0].length - 1 &&
        runoutCount === bubbleCount &&
        invalidPositionRunout.length === 0
      ) {
        this.$buefy.snackbar.open({
          message: 'AccuScan values imported successfully',
          type: 'is-success',
          position: 'is-top',
          duration: 10000,
        });
      }
    },

    getImagesRAT(selectedItem) {
      if (selectedItem.images && selectedItem.images.length > 0) {
        return selectedItem.images.length;
      }
      return 0;
    },

    importLaserScanValues(data) {
      this.inspections.sort((a, b) => a.number - b.number);

      // Laser scan index starts from 17
      const laserScanStartIndex = 17;
      const valuesInspectionCSV = [];
      const invalidColumns = [];

      for (let row = laserScanStartIndex; row < data.length; row++) {
        const [objName, controlName, , measured, deviation] = data[row];

        const colName = objName.replace(/ /g, '');
        const measurementPattern =
          /^(Diameter\s*(\d+)\s*[-:]?\s*Position\s*\(?(\d+)\)?|Length\s*(\d+)\s*[-:]?\s*Position\s*\(?(\d+)\)?)$/i;
        const runoutPattern =
          /^(Runout\s*(point)?\s*\(?(\d+)\)?|Runoutpoint\s*\(?(\d+)\)?)$/i;

        const genericMarkerPattern =
          /^(.+?)\s*(\d+)\s*[:-]?\s*Position\s*\(?(\d+)\)?$/i;

        let match = colName.match(measurementPattern);
        if (match) {
          const regexControlName = [
            'diameter',
            'length',
            'y distance',
            'x distance',
            'z distance',
          ];
          const isValidControlName = regexControlName.includes(
            controlName.trim().toLowerCase()
          );

          if (!isValidControlName) {
            invalidColumns.push(colName);
          }

          // If it's Diameter
          if (match[2] && match[3] && isValidControlName) {
            const bubbleNumber = parseInt(match[2], 10);
            const positionNumber = parseFloat(match[3]);
            const measuredValue =
              typeof measured === 'number'
                ? measured.toFixed(4)
                : measured || '';
            this.pushInspetionsCSV(
              valuesInspectionCSV,
              {
                type: 'diameter',
                number: bubbleNumber,
              },
              {
                number: positionNumber,
                value: measuredValue,
              }
            );

            // If it's Length
          } else if (match[4] && match[5] && isValidControlName) {
            const bubbleNumber = parseInt(match[4], 10); // match[4];
            const positionNumber = parseFloat(match[5]); // match[5];
            const measuredValue =
              typeof measured === 'number'
                ? measured.toFixed(4)
                : measured || '';
            this.pushInspetionsCSV(
              valuesInspectionCSV,
              {
                type: 'length',
                number: bubbleNumber,
              },
              {
                number: positionNumber,
                value: measuredValue,
              }
            );
          }
        } else {
          match = colName.match(runoutPattern);
          if (match) {
            const regexControlName = ['X', 'Y', 'Z', 'R', 'T'];
            const isValidControlName = regexControlName.includes(
              controlName.trim().toUpperCase()
            );

            if (!isValidControlName) {
              invalidColumns.push(colName);
            }
            // If it's Runout
            if (match[3] && isValidControlName) {
              const bubbleNumber = parseInt(match[3], 10); // match[6];
              const deviationValue =
                typeof deviation === 'number'
                  ? deviation.toFixed(4)
                  : deviation || '';
              this.pushInspetionsCSV(
                valuesInspectionCSV,
                {
                  type: 'runout',
                  number: bubbleNumber,
                },
                {
                  value: deviationValue,
                }
              );
            }
          }
        }
        if (controlName.trim().toUpperCase() === 'GENERIC MARKER') {
          match = colName.match(genericMarkerPattern);
          if (match) {
            // If it's generic
            if (match[2] && match[3]) {
              const bubbleNumber = parseInt(match[2], 10);
              const positionNumber = parseFloat(match[3]);
              const measuredValue =
                typeof measured === 'number'
                  ? measured.toFixed(4)
                  : measured || '';
              this.pushInspetionsCSV(
                valuesInspectionCSV,
                {
                  type: 'generic',
                  number: bubbleNumber,
                },
                {
                  number: positionNumber,
                  value: measuredValue,
                }
              );
            }
          }
        }
      }
      valuesInspectionCSV.sort((a, b) => a.number - b.number);

      // Check if the CSV file has the same number of inspections
      const inspectionsCount = this.inspections.reduce((count, item) => {
        if (
          item.type === 'diameter' ||
          item.type === 'length' ||
          item.type === 'runout' ||
          item.type === 'generic'
        ) {
          count++;
        }
        return count;
      }, 0);
      let valueI = 0;
      const mismatchedBubbles = [];
      this.inspections.forEach((inspection) => {
        if (
          inspection.type === 'diameter' ||
          inspection.type === 'length' ||
          inspection.type === 'runout' ||
          inspection.type === 'generic'
        ) {
          const valueIndex = valuesInspectionCSV.findIndex((value) => {
            return (
              value.number === inspection.number &&
              value.type === inspection.type
            );
          });

          if (valueIndex !== -1) {
            valuesInspectionCSV[valueIndex].measurements.sort(
              (a, b) => a.number - b.number
            );
            if (
              inspection.measurementCount ===
                valuesInspectionCSV[valueIndex].measurements.length &&
              (inspection.type === 'diameter' || inspection.type === 'length')
            ) {
              inspection.measurements.forEach((m, indexM) => {
                const csvMeasurement =
                  valuesInspectionCSV[valueIndex].measurements[indexM];
                m.value = csvMeasurement?.value ?? '';
              });

              valueI++;
            } else if (
              inspection.measurementCount !==
                valuesInspectionCSV[valueIndex].measurements.length &&
              (inspection.type === 'diameter' || inspection.type === 'length')
            ) {
              // Mismatch detected for diameter or length
              mismatchedBubbles.push({
                number: inspection.number,
                type: inspection.type,
                expected: inspection.measurementCount,
                found: valuesInspectionCSV[valueIndex].measurements.length,
              });
            } else if (inspection.type === 'runout') {
              inspection.measurements[1].value = '';
              const csvMeasurement =
                valuesInspectionCSV[valueIndex].measurements[0];
              inspection.measurements[0].value = csvMeasurement?.value ?? '';
              valueI++;
            } else if (
              inspection.measurementCount ===
                valuesInspectionCSV[valueIndex].measurements.length &&
              inspection.type === 'generic'
            ) {
              inspection.measurements.forEach((m, indexM) => {
                const csvMeasurement =
                  valuesInspectionCSV[valueIndex].measurements[indexM];
                m.value = csvMeasurement?.value ?? '';
              });

              valueI++;
            } else if (
              inspection.measurementCount !==
                valuesInspectionCSV[valueIndex].measurements.length &&
              inspection.type === 'generic'
            ) {
              // Mismatch detected for generic
              mismatchedBubbles.push({
                number: inspection.number,
                type: inspection.type,
                expected: inspection.measurementCount,
                found: valuesInspectionCSV[valueIndex].measurements.length,
              });
            }
          }
        }
      });

      const additionalMessages = [];

      if (mismatchedBubbles.length > 0) {
        additionalMessages.push(
          'Number of positions mismatch:',
          ...mismatchedBubbles.map(
            (bubble) =>
              `• Bubble ${bubble.number} (${bubble.type}): Expected ${bubble.expected}, found ${bubble.found}`
          )
        );
      }

      if (invalidColumns.length > 0) {
        if (additionalMessages.length > 0) {
          additionalMessages.push('');
        }
        additionalMessages.push(
          'Incorrect control names:',
          ...invalidColumns.map((col) => `• ${col}`)
        );
      }

      const additionalInfo =
        additionalMessages.length > 0
          ? `<br><br>${additionalMessages.join('<br>')}`
          : '';

      if (valueI === 0) {
        this.$buefy.snackbar.open({
          message: 'No inspection values were imported.',
          type: 'is-warning',
          position: 'is-top',
          pauseOnHover: true,
          duration: 10000,
        });
      } else if (
        inspectionsCount < valuesInspectionCSV.length &&
        inspectionsCount === valueI
      ) {
        this.$buefy.snackbar.open({
          message: `${this.$t(
            'diagram_builder.laserscan_import_success'
          )}<br>${this.$t(
            'diagram_builder.note_points_missing'
          )}${additionalInfo}`,
          type: 'is-warning',
          position: 'is-top',
          pauseOnHover: true,
          duration: 10000,
        });
      } else if (
        inspectionsCount < valuesInspectionCSV.length &&
        valueI < inspectionsCount
      ) {
        this.$buefy.snackbar.open({
          message: `${this.$t(
            'diagram_builder.laserscan_import_success'
          )}<br>${this.$t('diagram_builder.note_points_missing')}<br>${this.$t(
            'diagram_builder.not_all_dij_points_filled'
          )}${additionalInfo}`,
          type: 'is-warning',
          position: 'is-top',
          pauseOnHover: true,
          duration: 10000,
        });
      } else if (
        inspectionsCount < valuesInspectionCSV.length &&
        inspectionsCount > valueI
      ) {
        this.$buefy.snackbar.open({
          message: `${this.$t(
            'diagram_builder.laserscan_import_success'
          )}<br>${this.$t('diagram_builder.note_points_missing')}<br>${this.$t(
            'diagram_builder.not_all_dij_points_filled'
          )}${additionalInfo}`,
          type: 'is-warning',
          position: 'is-top',
          pauseOnHover: true,
          duration: 10000,
        });
      } else if (
        inspectionsCount === valuesInspectionCSV.length &&
        inspectionsCount > valueI
      ) {
        this.$buefy.snackbar.open({
          message: `${this.$t(
            'diagram_builder.laserscan_import_success'
          )}<br>${this.$t('diagram_builder.note_points_missing')}<br>${this.$t(
            'diagram_builder.not_all_dij_points_filled'
          )}${additionalInfo}`,
          type: 'is-warning',
          position: 'is-top',
          pauseOnHover: true,
          duration: 10000,
        });
      } else if (
        inspectionsCount > valuesInspectionCSV.length &&
        inspectionsCount > valueI
      ) {
        this.$buefy.snackbar.open({
          message: `${this.$t(
            'diagram_builder.laserscan_import_success'
          )}<br>${this.$t(
            'diagram_builder.not_all_dij_points_filled'
          )}${additionalInfo}`,
          type: 'is-warning',
          position: 'is-top',
          pauseOnHover: true,
          duration: 10000,
        });
      } else if (
        inspectionsCount === valueI &&
        valuesInspectionCSV.length === valueI
      ) {
        this.$buefy.snackbar.open({
          message: `${this.$t(
            'diagram_builder.all_inspection_values_imported_successfully'
          )}${additionalInfo}`,
          type: 'is-success',
          position: 'is-top',
          pauseOnHover: true,
          duration: 10000,
        });
      }

      this.$store.dispatch(SAVE_BUBBLES, {
        taskId: this.task._id,
        data: this.inspections,
      });

      this.initializeMarkers();
    },

    pushInspetionsCSV(valuesInspectionCSV, inspection, positionValue) {
      const inspectionIndex = valuesInspectionCSV.findIndex(
        (value) =>
          value.number === inspection.number && value.type === inspection.type
      );
      if (inspectionIndex !== -1) {
        valuesInspectionCSV[inspectionIndex].measurements.push(positionValue);
      } else {
        valuesInspectionCSV.push({
          ...inspection,
          measurements: [positionValue],
        });
      }
    },

    getNextInspectionNumber() {
      const inspectionNumbers = this.inspections
        .filter(
          (inspection) =>
            ![
              'text',
              'link',
              'arrow',
              'line',
              'rat',
              'position-image',
              'table',
            ].includes(inspection.type)
        )
        .map((inspection) => inspection.number);

      const allNumbers = [...inspectionNumbers];

      if (allNumbers.length === 0) {
        return 1; // No inspections or annotations, start from 1
      }

      allNumbers.sort((a, b) => a - b);

      const firstNumber = allNumbers[0];

      // Check if the first number is greater than 1, if so, return 1
      if (firstNumber > 1) {
        return 1;
      }

      // Iterate through the numbers to find the first gap
      for (let i = 0; i < allNumbers.length - 1; i++) {
        if (allNumbers[i + 1] - allNumbers[i] > 1) {
          // Return the first missing number if a gap is found
          return allNumbers[allNumbers.length - 1] + 1;
        }
      }

      // If no gap is found, return the next number after the last one
      return allNumbers[allNumbers.length - 1] + 1;
    },

    initializeMarkers() {
      this.scope.activate();
      this.inspections.forEach((inspection) => {
        if (inspection) {
          if (inspection.type === 'length') {
            this.makeLengthMarker(inspection);
          } else if (inspection.type === 'position-image') {
            if (!this.isZoomingBuubles) {
              this.makePositionImageMarker(inspection);
            }
          } else {
            this.makeSimpleMarker(inspection);
          }
        }
      });
      if (!this.isZoomingBuubles) {
        this.annotations.forEach((annotation) => {
          if (annotation.type === 'text') {
            this.makeTextMarker(annotation);
          } else if (annotation.type === 'link' && !this.reportMode) {
            this.makeLinkMarker(annotation);
          } else if (annotation.type === 'arrow') {
            this.makeArrowMarker(annotation);
          } else if (annotation.type === 'line') {
            this.makeLineMarker(annotation);
          }
        });
      }
    },
    chooseDiagramToImport() {
      if (this.readOnly) return;
      this.$refs.diagramChooser.click();
    },
    importDiagram(event) {
      if (this.readOnly) return;
      const { files } = event.target;
      if (files.length === 0) return;

      const [file] = files;
      const fileReader = new FileReader();

      const insertImage = (src) => {
        const image = new Image();
        image.onload = () => {
          this.isImageSelected = true;
          if (this.image) this.image.remove();
          this.image = new paper.Raster(image);
          this.scope.project.activeLayer.insertChild(0, this.image);
          this.image.position = this.scope.view.center;
        };
        image.src = src;
      };

      const handlePDF = () => {
        console.log('handing pdf');
        fileReader.onload = () => {
          getDocument({ data: fileReader.result })
            .promise.then((pdf) => pdf.getPage(1))
            .then((page) => {
              const viewport = page.getViewport({ scale: 1 });
              const canvas = document.createElement('canvas');
              const context = canvas.getContext('2d');
              canvas.width = viewport.width;
              canvas.height = viewport.height;

              return page
                .render({ canvasContext: context, viewport })
                .promise.then(() => {
                  const src = canvas.toDataURL();
                  insertImage(src);

                  canvas.toBlob((blob) => {
                    if (!blob) {
                      console.error('Canvas toBlob failed');
                      return;
                    }

                    const newFile = new File(
                      [blob],
                      `${file.name.replace(/\.\w+$/, '')}.png`,
                      { type: 'image/png' }
                    );

                    this.saveImageToS3({ file: newFile, src });
                  }, 'image/png');
                });
            })
            .catch((err) => {
              console.error('PDF render failed:', err);
            });
        };
        fileReader.readAsArrayBuffer(file);
      };

      const handleTIFF = () => {
        fileReader.onload = () => {
          const tiff = new Tiff({ buffer: fileReader.result });
          const canvas = tiff.toCanvas();
          insertImage(canvas.toDataURL());
        };
        fileReader.readAsArrayBuffer(file);
      };

      const handleImage = () => {
        fileReader.onload = () => {
          const src = fileReader.result;
          insertImage(src);
          this.saveImageToS3({ file, src });
        };
        fileReader.readAsDataURL(file);
      };

      switch (file.type) {
        case 'application/pdf':
          handlePDF();
          break;
        case 'image/tiff':
          handleTIFF();
          break;
        default:
          handleImage();
          break;
      }

      // Clear input to allow re-upload of the same file
      event.target.value = '';
    },

    async uploadToS3(file, path) {
      const dijConfigurations = new DIJVueConfigurations();
      return dijConfigurations.uploadFileToS3(file, file.name, path);
    },
    async saveImageToS3(data) {
      const path = `uploads/products/${new Date().getFullYear()}/${
        this.task._id
      }`;
      const url = await this.uploadToS3(data.file, path);

      this.$store.commit(SET_CURRENT_TASK_DIAGRAM_SOURCE, url);
      this.$store.dispatch(SAVE_CURRENT_TASK_STATE);
    },
    enableMode(mode) {
      if (this.readOnly) return;
      if (typeof mode === 'object') {
        if (!this[mode.enableMode]) return;
        this[mode.enableMode](mode.type);
      } else {
        if (!this[mode]) return;
        this[mode]();
      }
    },
    enableSelectMode() {
      this.explicitMoveMeasurementMode = false;
      this.isItemDetailVisible = false;
      this.mode = 'select';
      this.selectTool.activate();
    },
    enableMoveMode() {
      this.isItemDetailVisible = false;
      this.mode = 'move';
      this.moveTool.activate();
    },
    enableMoveMeasurementMode() {
      if (this.readOnly) return;
      this.explicitMoveMeasurementMode = !this.explicitMoveMeasurementMode;

      this.isItemDetailVisible = false;
      this.mode = 'select';
      this.selectTool.activate();
    },
    enableDiameterMode() {
      this.isItemDetailVisible = false;
      this.mode = 'diameter';
      this.diameterTool.activate();
    },
    enableRunoutMode() {
      this.isItemDetailVisible = false;
      this.mode = 'runout';
      this.runoutTool.activate();
    },
    enableLinearLengthMode() {
      this.isItemDetailVisible = false;
      this.mode = 'linearLength';
      this.linearLengthTool.activate();
    },
    enableAlignedLengthMode() {
      this.isItemDetailVisible = false;
      this.mode = 'alignedLength';
      this.alignedLengthTool.activate();
    },
    enableVisualMode() {
      this.isItemDetailVisible = false;
      this.mode = 'visual';
      this.visualTool.activate();
    },
    enableGenericMode() {
      this.isItemDetailVisible = false;
      this.mode = 'generic';
      this.genericTool.activate();
    },
    enableFreefindingMode() {
      if (this.readOnly) return;
      if (this.mode !== 'freefinding') {
        this.isItemDetailVisible = false;
        this.mode = 'freefinding';
        this.freeFindingTool.activate();
      } else {
        this.enableSelectMode();
      }
    },
    enablePositionImage(type) {
      this.enablePositionImageType = type;
      this.isItemDetailVisible = false;
      this.mode = 'positionImage';
      this.positionImageTool.activate();
    },
    enableTextMode() {
      this.isItemDetailVisible = false;
      this.mode = 'text';
      this.textTool.activate();
    },
    enableLinkMode() {
      this.mode = 'link';
      this.linkTool.activate();
    },
    enableTableMode() {
      this.isTableModeActive = !this.isTableModeActive;
      this.isItemDetailVisible = false;
    },
    enableRATMode() {
      if (this.readOnly) return;
      if (this.mode !== 'rat') {
        this.isItemRATVisible = false;
        this.mode = 'rat';
        this.ratTool.activate();
      } else {
        this.enableSelectMode();
      }
    },
    enableArrowMode() {
      this.isItemDetailVisible = false;
      this.mode = 'arrow';
      this.arrowTool.activate();
    },
    enableLineMode() {
      this.isItemDetailVisible = false;
      this.mode = 'line';
      this.lineTool.activate();
    },
    replaceImage() {
      if (this.readOnly) return;
      if (this.isTaskCompleted) {
        this.taskCompletedError();
        return;
      }
      this.isItemDetailVisible = false;
      this.$refs.diagramChooser.click();
    },
    zoomDefault() {
      this.scope.view.zoom = 1;
      this.onZoomChange();
    },
    zoomIn() {
      this.scope.view.zoom *= 1.2;
      this.onZoomChange();
    },
    zoomDefaultBubble() {
      this.squareSizeBubble = 35;
      this.fontSizeBubble = 20;
      this.initializeMarkers();
      this.onZoomChange();
    },
    zoomInBubble() {
      this.squareSizeBubble += 2;
      this.fontSizeBubble += 1;
      this.isZoomingBuubles = true;
      this.initializeMarkers();
      this.onZoomChange();
      this.isZoomingBuubles = false;
    },
    zoomOutBubble() {
      if (this.squareSizeBubble === 15 && this.fontSizeBubble === 10) return;
      this.fontSizeBubble -= 1;
      this.squareSizeBubble -= 2;
      this.isZoomingBuubles = true;
      this.initializeMarkers();
      this.onZoomChange();
      this.isZoomingBuubles = false;
    },
    zoomOut() {
      this.scope.view.zoom *= 0.8;
      this.onZoomChange();
    },
    onZoomChange() {
      const imageZoom = this.scope.view.zoom;
      const { squareSizeBubble: circleRadiusBubble, fontSizeBubble } = this;
      this.$emit('zoomChange', {
        imageZoom,
        circleRadiusBubble,
        fontSizeBubble,
      });
    },
    cancel() {
      this.$emit('cancel');
    },
    ok() {
      const { canvas } = this.$refs;
      this.$emit('ok', canvas);
    },

    async deleteInspection() {
      if (this.readOnly) return;
      if (!this.selectedItem._id) {
        console.warn('No selected item to delete');
      }

      this.isLoadingRatInspection = true;
      // To delete the rat entry
      if (this.selectedItem.type === 'rat') {
        await this.$store.dispatch(GET_JOB, this.$route.params.id);
        const job = this.$store.state.JobsModule.currentJob;

        let row = null;

        if (job.repairAssessment)
          row = job.repairAssessment.find(
            (rat) => rat.id === this.selectedItem.ratRowID
          );

        if (row) {
          this.$store.dispatch(DELETE_REPAIR_ASSESSMENT_ROW, {
            job,
            row,
            tool: this.tool,
          });
        }
      }

      this.$store.dispatch(DELETE_BUBBLE, this.selectedItem._id);

      if (this.selectedItem.type === 'rat') {
        this.$buefy.snackbar.open({
          duration: 5000,
          message: 'RAT entry successfully deleted',
          type: 'is-info',
          position: 'is-bottom-left',
          queue: false,
        });
      } else {
        this.$buefy.snackbar.open({
          duration: 5000,
          message: `${
            this.selectedItem.type.charAt(0).toUpperCase() +
            this.selectedItem.type.slice(1)
          } deleted successfully`,
          type: 'is-info',
          position: 'is-bottom-left',
          queue: false,
        });
      }

      this.deleteInspectionVisuals(this.selectedItem);
    },
    deleteInspectionVisuals(deletedItem) {
      const markerItem = [...this.inspections, ...this.annotations].find(
        (inspection) => inspection._id === deletedItem._id
      );

      if (markerItem?.marker) {
        markerItem.marker.remove();
      }

      this.inspections = this.inspections.filter(
        (inspection) => inspection._id !== deletedItem._id
      );

      this.annotations = this.annotations.filter(
        (annotation) => annotation._id !== deletedItem._id
      );

      this.isItemDetailVisible = false;
      this.isItemRATVisible = false;
      this.isLoadingRatInspection = false;
    },
    wsBubbleDeleted(deletedBubble) {
      if (deletedBubble.taskId !== this.task._id) {
        return;
      }

      this.deleteInspectionVisuals(deletedBubble);
    },
    wsBubbleUpdated(updatedBubble, preferLocal = false) {
      if (updatedBubble.taskId !== this.task._id) {
        return;
      }
      setConflictSnapshot(CONFLICT_ENTITY_TYPES.BUBBLE, updatedBubble);
      const targetList = this.getTargetList(updatedBubble.type);
      // Find and update the bubble, retaining the `.marker` property
      const index = targetList.findIndex(
        (item) => item._id === updatedBubble._id
      );

      if (index !== -1) {
        const existingItem = targetList[index];
        if (existingItem.type === 'rat') {
          const job = this.$store.state.JobsModule.currentJob;

          let row = {};

          if (job.repairAssessment)
            row = job.repairAssessment.find(
              (rat) => rat.id === existingItem.ratRowID
            );

          // Assigning the RAT row into the selected item without braking the reactivity
          // eslint-disable-next-line no-restricted-syntax
          for (const key in row) {
            // eslint-disable-next-line no-prototype-builtins
            if (row.hasOwnProperty(key)) {
              updatedBubble[key] = row[key];
            }
          }
        }

        this.replaceItemProps(targetList, updatedBubble, [], preferLocal);

        const mergedItem = targetList[index];

        if (mergedItem.type === 'link') {
          mergedItem.text = updatedBubble.text;
          mergedItem.internalDocumentData = updatedBubble.internalDocumentData;
        }

        this.remakeMarker(
          mergedItem,
          this.selectedItem && this.selectedItem._id === mergedItem._id
        );

        if (this.selectedItem && this.selectedItem._id === mergedItem._id) {
          this.selectedItem = mergedItem;
        }
      } else {
        targetList.push(updatedBubble);
        this.remakeMarker(updatedBubble);
      }

      return updatedBubble;
    },
    async wsBubblesCreated() {
      const data = await this.$store.dispatch(GET_BUBBLES, this.task._id);

      this.inspections.forEach((inspection) => {
        if (inspection.marker) inspection.marker.remove();
      });

      this.inspections = Array.isArray(data)
        ? data.map((inspection) => ({
            ...inspection,
            measurements: inspection.measurements?.map((m) => ({ ...m })) || [],
          }))
        : [];

      this.inspections.forEach((inspection) => {
        if (inspection?.inTable && Array.isArray(inspection.measurements)) {
          inspection.measurements = [];
        }
      });

      this.initializeMarkers();
    },
    async handleBubbleUpdate(selectedItem) {
      const isInspector = this.tool === 'inspector';

      this.isLoadingBubbleUpdates = true;
      const result = await this.$store.dispatch(UPDATE_BUBBLE, {
        ...new BubbleDTO(selectedItem),
        isInspector,
      });

      switch (result.status) {
        case 200:
          this.wsBubbleUpdated(result.data, true);
          break;
        case 404:
          this.$buefy.toast.open({
            message: 'Bubble not found. Please refresh the page.',
            position: 'is-bottom',
            type: 'is-danger',
          });
          break;
        case 202:
        case 409:
          break;
        default:
          this.$buefy.toast.open({
            message: 'Something went wrong while updating the bubble',
            position: 'is-bottom',
            type: 'is-danger',
          });
          break;
      }

      this.isLoadingBubbleUpdates = false;
    },
    async closeInspection() {
      if (this.isItemDetailVisible || this.isItemRATVisible) {
        this.isItemDetailVisible = false;
        this.isItemRATVisible = false;

        if (this.readOnly) {
          return;
        }

        if (this.selectedItem._id) {
          try {
            if (this.selectedItem.type === 'link')
              this.isLoadingRatBubble = true;
            await this.handleBubbleUpdate(this.selectedItem);
          } catch (error) {
            console.error(error);
          } finally {
            this.isLoadingRatBubble = false;
          }
        }
      }
    },

    async saveInspectionRat() {
      if (this.readOnly) {
        this.closeInspection();
        return;
      }
      this.isLoadingRatInspection = true;
      try {
        if (!this.selectedItem.ratRowID) {
          const ratRowID = await this.addRATRow(
            this.$refs.refRatDetail.isMinor
          );
          this.selectedItem.ratRowID = ratRowID;
        }

        // await this.$store.dispatch(GET_JOB, this.$route.params.id);
        const job = this.$store.state.JobsModule.currentJob;
        let row = {};
        if (job.repairAssessment && job.repairAssessment.length > 0) {
          const rowRat = job.repairAssessment.find(
            (rat) => rat.id === this.selectedItem.ratRowID
          );
          if (rowRat) row = rowRat;
        }

        if (this.$refs.refRatDetail.isMinor) {
          row.assembly = this.selectedItem.assembly
            ? this.selectedItem.assembly
            : '';
          row.itemNumber = this.selectedItem.itemNumber
            ? this.selectedItem.itemNumber
            : '';
          row.partName = this.selectedItem.partName
            ? this.selectedItem.partName
            : '';
          row.partNumber = this.selectedItem.partNumber;
          row.quantity = this.selectedItem.quantity;
          row.reuse = this.selectedItem.reuse;
          row.repair = this.selectedItem.repair;
          row.replace = this.selectedItem.replace;
          row.comments = this.selectedItem.comments;
          row.isMinor = true;
        } else {
          row.level = this.selectedItem.level ? this.selectedItem.level : 1;
          row.assembly = this.selectedItem.assembly;
          row.itemNumber = this.selectedItem.itemNumber;
          row.partName = this.selectedItem.partName;
          row.partNumber = this.selectedItem.partNumber;
          row.quantity = this.selectedItem.quantity;
          row.visual = this.selectedItem.visual;
          row.dim = this.selectedItem.dim;
          row.ndt = this.selectedItem.ndt;
          row.reuse = this.selectedItem.reuse;
          row.repair = this.selectedItem.repair;
          row.replace = this.selectedItem.replace;
          row.missing = this.selectedItem.missing;
          row.summary = this.selectedItem.summary;
          row.repairScope = this.selectedItem.repairScope;
          row.estimatedLabor = this.selectedItem.estimatedLabor;
          row.received = this.selectedItem.received;
          row.images = this.selectedItem.images;
          row.isMinor = false;
        }

        row.bubbleId = this.selectedItem._id;

        await this.handleBubbleUpdate(this.selectedItem);
        const updateRowResponse = await this.$store.dispatch(
          UPDATE_REPAIR_ASSESSMENT_ROW,
          {
            job,
            row,
            tool: this.tool,
          }
        );

        if (job && job.repairAssessment) {
          const idx = job.repairAssessment.findIndex(
            (rat) => rat.id === updateRowResponse.id
          );
          if (idx !== -1) {
            this.$set(job.repairAssessment, idx, updateRowResponse);
          }
        }

        await this.$store.dispatch('unlockRepairAssessmentRow', {
          job,
          row,
          tool: this.tool,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoadingRatInspection = false;
        this.closeInspection();
      }
    },
    async addNewInspection() {
      if (this.readOnly) return;
      this.processingNewBubble = true;

      const snapshotItem = { ...this.selectedItem };

      const createdBubble = await this.$store.dispatch(CREATE_BUBBLE, {
        taskId: this.task._id,
        bubble: snapshotItem,
      });

      this.selectedItem = {
        ...this.selectedItem,
        ...createdBubble,
        id: createdBubble._id,
      };

      const isAnnotation = ['text', 'arrow', 'line', 'link'].includes(
        this.selectedItem.type
      );
      const targetList = isAnnotation ? this.annotations : this.inspections;

      targetList.push(this.selectedItem);

      this.processingNewBubble = false;
    },
    initializeSelectTool() {
      this.selectTool = new paper.Tool();
      this.selectTool.onMouseDown = (event) => {
        // Ignore image hits
        if (this.isSelectingMarker(event) && !this.reportMode) {
          this.handleOnMouseDownMarkerSelection(event);
        } else {
          this.isMovingImage = true;
        }
      };
      let initialPoint;
      // Don't allow drag behavior in inspector mode
      this.selectTool.onMouseDrag = (event) => {
        if (!this.initialDragPoint) {
          // Save the first point when the drag starts
          this.initialDragPoint = event.point;
        }

        const dragDistance = Math.sqrt(
          (event.point.x - this.initialDragPoint.x) ** 2 +
            (event.point.y - this.initialDragPoint.y) ** 2
        );

        // Use this distance to ensure that user wants to move the bubble not just to open it
        if (dragDistance > 1 && !this.readOnly) {
          // User intends to move the bubble
          this.bubbleWasMoved = true;
        }

        if (this.isMovingImage) {
          this.moveImageTo(event.lastPoint, event.point);
        } else if (
          (!this.isInInspectorMode() ||
            this.selectedItem.type === 'freefinding') &&
          this.isMouseButtonDown &&
          !this.isTaskCompleted &&
          !this.readOnly
        ) {
          // Save the first point as the initial point
          if (!initialPoint) {
            initialPoint = event.point;
          }
          if (
            this.isMovingMarker ||
            this.isOutsideMoveDeadzone(initialPoint, event.point)
          ) {
            this.isMovingMarker = true;
            if (this.selectedItem.type === 'length') {
              if (this.explicitMoveMeasurementMode) {
                const dx =
                  event.point.x -
                  this.selectedItem.marker.children[1].position.x;
                const dy =
                  event.point.y -
                  this.selectedItem.marker.children[1].position.y;
                const actualPoint = {
                  x: this.selectedItem.marker.position.x + dx,
                  y: this.selectedItem.marker.position.y + dy,
                };
                this.selectedItem.marker.position = actualPoint;

                this.selectedItem.firstPoint = this.getImagePoint(
                  this.selectedItem.marker.children[0].segments[0].point
                );
                this.selectedItem.secondPoint = this.getImagePoint(
                  this.selectedItem.marker.children[0].segments[1].point
                );
                this.selectedItem.thirdPoint = this.getImagePoint(
                  this.selectedItem.marker.children[0].segments[2].point
                );
                this.selectedItem.fourthPoint = this.getImagePoint(
                  this.selectedItem.marker.children[0].segments[3].point
                );
                this.selectedItem.point = this.getImagePoint(
                  this.selectedItem.marker.children[1].position
                );
              } else {
                const initialLengthPoint =
                  this.selectedItem.marker.children[0].segments[0].point;
                const finalPoint =
                  this.selectedItem.marker.children[0].segments[3].point;
                if (this.selectedItem.lengthType === 'aligned') {
                  // Make line and circle
                  const circleRadius = this.squareSizeBubble / 2;
                  const points = this.calculateParallelPoints(
                    initialLengthPoint,
                    finalPoint,
                    event.point
                  );
                  const path = new paper.Path(
                    initialLengthPoint,
                    points.middlePoint1,
                    points.middlePoint2,
                    finalPoint
                  );
                  // Convert middle points to Paper.js Point objects for easy calculations
                  const p1 = new paper.Point(
                    points.middlePoint1.x,
                    points.middlePoint1.y
                  );
                  const p2 = new paper.Point(
                    points.middlePoint2.x,
                    points.middlePoint2.y
                  );
                  const mousePoint = event.point;
                  // Calculate vectors
                  const lineVector = p2.subtract(p1);
                  const mouseVector = mousePoint.subtract(p1);
                  // Project mouseVector onto lineVector
                  let projection = lineVector
                    .normalize()
                    .multiply(mouseVector.dot(lineVector.normalize()));
                  // Check if projection exceeds the line segment
                  if (projection.length > lineVector.length) {
                    projection = lineVector; // Snap to p2 if beyond
                  } else if (projection.dot(lineVector) < 0) {
                    projection = new paper.Point(0, 0); // Snap to p1 if before
                  }
                  // Calculate new circle position
                  const circlePosition = p1.add(projection);
                  const circle = new paper.Path.Circle(
                    circlePosition,
                    circleRadius
                  );
                  this.lengthMode = 0;
                  path.strokeColor = '#000';
                  path.strokeWidth = 2;
                  circle.strokeWidth = 0;
                  circle.fillColor = '#000';
                  // Make label
                  const label = new paper.PointText();
                  label.fontStyle = 'normal';
                  label.fillColor = 'white';
                  label.content = this.selectedItem.number;
                  label.fontFamily = 'Siemens Roman';
                  label.fontSize = this.fontSizeBubble;
                  label.position = circlePosition;
                  // label.fontWeight = 'bold';
                  // Remove previous marker and create new one
                  this.selectedItem.marker.remove();
                  this.selectedItem.marker = new paper.Group(
                    path,
                    circle,
                    label
                  );
                } else {
                  this.addDelta(event.delta);
                  const mode = this.determineMode(event);
                  const middlePoints = this.calculateMiddlePoints(
                    mode,
                    initialLengthPoint,
                    finalPoint,
                    event.point
                  );
                  const { path, circle, circlePosition } =
                    this.createPathAndCircle(
                      initialLengthPoint,
                      finalPoint,
                      middlePoints,
                      event.point,
                      mode
                    );
                  // Set path properties
                  path.strokeColor = '#000';
                  path.strokeWidth = 2;
                  // Set circle properties
                  circle.strokeWidth = 0;
                  circle.fillColor = '#000';
                  // Make label
                  const fontSize = this.fontSizeBubble;
                  const label = new paper.PointText({
                    fillColor: 'white',
                    content: this.selectedItem.number,
                    fontFamily: 'Siemens Roman',
                    fontSize,
                    position: circlePosition,
                    fontWeight: 'bold',
                    fontStyle: 'normal',
                  });
                  // Update length mode based on current mode
                  this.lengthMode = mode === 'horizontal' ? 0 : 1;
                  // Remove previous marker and create new one
                  this.selectedItem.marker.remove();
                  this.selectedItem.marker = new paper.Group(
                    path,
                    circle,
                    label
                  );
                }
                this.selectedItem.secondPoint = this.getImagePoint(
                  this.selectedItem.marker.children[0].segments[1].point
                );
                this.selectedItem.thirdPoint = this.getImagePoint(
                  this.selectedItem.marker.children[0].segments[2].point
                );
                this.selectedItem.point = this.getImagePoint(
                  this.selectedItem.marker.children[1].position
                );
              }
            } else if (['arrow', 'line'].includes(this.selectedItem.type)) {
              if (this.explicitMoveMeasurementMode) {
                this.selectedItem.marker.position = event.point;
                this.selectedItem.point = this.getImagePoint(
                  this.selectedItem.marker.children[0].segments[1].point
                );
                const startPoint =
                  this.selectedItem.marker.children[0].segments[0].point;
                this.selectedItem.startPoint = this.getImagePoint(startPoint);
              } else {
                const initialArrowPoint =
                  this.selectedItem.marker.children[0].segments[0].point;
                this.selectedItem.marker.remove();
                if (this.shiftPressed) {
                  let endPoint = event.point;
                  const vector = endPoint.subtract(initialArrowPoint);
                  const { angle } = vector;
                  const snappedAngle = Math.round(angle / 45) * 45;
                  vector.angle = snappedAngle;
                  endPoint = initialArrowPoint.add(vector);
                  this.arrowEndPoint = endPoint;
                  const path = new paper.Path(initialArrowPoint, endPoint);
                  path.strokeColor = '#000';
                  path.strokeWidth = 2;
                  this.selectedItem.marker = new paper.Group(path);

                  const snapHelper = document.getElementById(
                    'arrow-circle-snap-helper'
                  );
                  if (snapHelper) {
                    // Convert Paper.js point → canvas pixel coordinates
                    const viewPoint =
                      this.scope.view.projectToView(initialArrowPoint);

                    // Get canvas position in the page
                    const canvasRect =
                      this.scope.view.element.getBoundingClientRect();

                    // Final DOM position
                    const left = canvasRect.left + viewPoint.x - 50;
                    const top = canvasRect.top + viewPoint.y - 150;

                    snapHelper.style.left = `${left}px`;
                    snapHelper.style.top = `${top}px`;
                  }
                } else {
                  const path = new paper.Path(initialArrowPoint, event.point);
                  path.strokeColor = '#000';
                  path.strokeWidth = 2;
                  this.selectedItem.marker = new paper.Group(path);
                }
                this.selectedItem.point = this.getImagePoint(
                  this.selectedItem.marker.children[0].segments[1].point
                );
                if (this.selectedItem.type === 'arrow') {
                  this.makeArrowMarker(this.selectedItem);
                }
              }
            } else if (
              this.selectedItem.type === 'text' ||
              this.selectedItem.type === 'link'
            ) {
              this.selectedItem.marker.position = event.point;
              this.selectedItem.point = this.getImagePoint(
                this.selectedItem.marker.children[0].position
              );
            } else if (this.selectedItem.type === 'position-image') {
              this.selectedItem.marker.position = event.point;
              this.selectedItem.point = this.getImagePoint(
                this.selectedItem.marker.position
              );
            } else if (this.selectedItem.type === 'table') {
              this.selectedItem.marker.position = event.point;
              this.selectedItem.point = this.getImagePoint(
                this.selectedItem.marker.position
              );
            } else {
              const { selectedItem } = this;

              // To keep the marker pointing to user mouse
              // offset the mrket acording to marker orientation
              const positionToPointMarker = {
                x: event.point.x,
                y: event.point.y,
              };
              if (selectedItem.orientation === 'up') {
                positionToPointMarker.y -=
                  selectedItem.marker.bounds.height / 2;
              } else if (selectedItem.orientation === 'down') {
                positionToPointMarker.y +=
                  selectedItem.marker.bounds.height / 2;
              } else if (selectedItem.orientation === 'right') {
                positionToPointMarker.x += selectedItem.marker.bounds.width / 2;
              } else if (selectedItem.orientation === 'left') {
                positionToPointMarker.x -= selectedItem.marker.bounds.width / 2;
              }

              this.selectedItem.marker.position = positionToPointMarker;
              this.selectedItem.point = this.getImagePoint(event.point);
            }
          }
        }
      };
      this.selectTool.onMouseUp = async (event) => {
        this.initialDragPoint = null;
        initialPoint = null;
        const wasMovingImage = this.isMovingImage;
        this.isMovingImage = false;
        this.isMouseButtonDown = false;
        if (!wasMovingImage) {
          if (
            this.isInInspectorMode() &&
            this.selectedItem &&
            this.selectedItem.type === 'link'
          ) {
            this.onOpenLinkFile(this.selectedItem.internalDocumentData[0]);
          }
          if (this.showToolbar && this.selectedItem) {
            const windowPoint = {
              x: event.event.clientX,
              y: event.event.clientY,
            };

            if (!this.bubbleWasMoved) {
              this.showInspectionDetail(windowPoint);
            }

            // if (this.selectedItem.type === 'arrow') {
            // this.makeArrowMarker(this.selectedItem);
            // }

            // If there a switchBackTool, switch back to it
            if (this.switchBackTool) {
              this.switchBackTool.activate();
              this.switchBackTool = null;
            }
          } else if (
            this.isInInspectorMode() &&
            this.selectedItem &&
            this.showList
          ) {
            if (!this.selectedItem.inTable)
              this.$refs.list.selectInspection(this.selectedItem);
            const windowPoint = {
              x: event.event.clientX,
              y: event.event.clientY,
            };

            this.showInspectionDetail(windowPoint);
          } else if (
            this.selectedItem &&
            this.selectedItem.type === 'rat' &&
            !this.bubbleWasMoved
          ) {
            const windowPoint = {
              x: event.event.clientX,
              y: event.event.clientY,
            };

            this.showInspectionDetail(windowPoint);
          } else if (
            this.isInInspectorMode() &&
            this.reportMode &&
            this.selectedItem &&
            this.selectedItem.type !== 'rat'
          ) {
            return;
          } else if (
            this.isInInspectorMode() &&
            this.selectedItem &&
            this.selectedItem.type !== 'rat'
          ) {
            this.selectedItem = null;
          }

          if (!this.readOnly && (this.bubbleWasMoved || this.isMovingMarker)) {
            await this.handleBubbleUpdate(this.selectedItem);
          }
        }

        this.bubbleWasMoved = false;
        this.isMovingMarker = false;
      };
    },
    initializeMoveTool() {
      this.moveTool = new paper.Tool();
      this.moveTool.onMouseDown = () => {
        this.isMouseButtonDown = true;
      };
      this.moveTool.onMouseUp = () => {
        this.isMouseButtonDown = false;
      };
      this.moveTool.onMouseDrag = (event) => {
        if (this.isMouseButtonDown) {
          this.moveImageTo(event.lastPoint, event.point);
        }
      };
    },
    showProcessingBubblePopup() {
      this.$buefy.snackbar.open({
        duration: 5000,
        message: 'Hold on! The system is handling the last operation.',
        pauseOnHover: true,
        type: 'is-link',
        queue: false,
      });
    },
    initializeDiameterTool() {
      this.diameterTool = new paper.Tool();
      this.diameterTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }
        if (this.isSelectingMarker(event)) {
          this.delegateToSelectTool(event);
        } else {
          if (this.processingNewBubble) {
            this.showProcessingBubblePopup();
            return;
          }
          const { isTableModeActive } = this;
          this.selectedItem = {
            type: 'diameter',
            number: this.getNextInspectionNumber(),
            unit: this.user.unit === 'inch' ? 'in' : this.user.unit,
            expectedValue: '0.0000',
            decimalPlaces: 4,
            toleranceType: 'normal',
            positiveTolerance: '0.0000',
            negativeTolerance: '0.0000',
            orientation: 'up',
            measurementCount: 1,
            measurements: !isTableModeActive ? [EMPTY_MEASUREMENT_VALUE] : [],
            multipleMeasurements: false,
            point: this.getImagePoint(event.point),
            description: '',
            tableMeasurements: [[EMPTY_MEASUREMENT_VALUE]],
            inTable: isTableModeActive,
          };

          // Copy the data from the last marker of the same type
          const diameterInspections = this.inspections.filter(
            (inspection) => inspection.type === 'diameter'
          );
          if (diameterInspections.length > 0) {
            const lastDiameterInspection =
              diameterInspections[diameterInspections.length - 1];
            this.copyMarkerData(lastDiameterInspection, this.selectedItem);
          }

          this.addNewInspection();

          this.makeSimpleMarker(this.selectedItem);

          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          this.showInspectionDetail(windowPoint);
        }
      };
    },
    initializeRunoutTool() {
      this.runoutTool = new paper.Tool();
      this.runoutTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }
        if (this.isSelectingMarker(event)) {
          this.delegateToSelectTool(event);
        } else {
          if (this.processingNewBubble) {
            this.showProcessingBubblePopup();
            return;
          }
          const { isTableModeActive } = this;
          this.selectedItem = {
            id: uuidv4(),
            type: 'runout',
            number: this.getNextInspectionNumber(),
            unit: this.user.unit === 'inch' ? 'in' : this.user.unit,
            tolerance: '0.0000',
            decimalPlaces: 4,
            directionalUnit: 'deg',
            direction: '0',
            description: '',
            orientation: 'up',
            measurementCount: 2,
            measurements: isTableModeActive
              ? []
              : [EMPTY_MEASUREMENT_VALUE, EMPTY_MEASUREMENT_VALUE],
            multipleMeasurements: false,
            point: this.getImagePoint(event.point),
            tableMeasurements: [
              [EMPTY_MEASUREMENT_VALUE, EMPTY_MEASUREMENT_VALUE],
            ],
            inTable: isTableModeActive,
          };

          // Copy the data from the last marker of the same type
          const runoutInspections = this.inspections.filter(
            (inspection) => inspection.type === 'runout'
          );
          if (runoutInspections.length > 0) {
            const lastRunoutInspection =
              runoutInspections[runoutInspections.length - 1];
            this.copyMarkerData(lastRunoutInspection, this.selectedItem);
          }

          this.addNewInspection();
          this.makeSimpleMarker(this.selectedItem);

          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          this.showInspectionDetail(windowPoint);
        }
      };
    },

    calculateMiddlePoints(mode, initialPoint, finalPoint, eventPoint) {
      if (mode === 'horizontal') {
        return [
          new paper.Point(initialPoint.x, eventPoint.y),
          new paper.Point(finalPoint.x, eventPoint.y),
        ];
      }

      // mode === 'vertical'
      return [
        new paper.Point(eventPoint.x, initialPoint.y),
        new paper.Point(eventPoint.x, finalPoint.y),
      ];
    },

    createPathAndCircle(
      initialPoint,
      finalPoint,
      middlePoints,
      eventPoint,
      mode
    ) {
      const path = new paper.Path(initialPoint, ...middlePoints, finalPoint);
      const circleRadius = this.squareSizeBubble / 2;
      let circlePosition;

      if (mode === 'horizontal') {
        circlePosition = new paper.Point(
          (initialPoint.x + finalPoint.x) / 2,
          eventPoint.y
        );
      } else {
        // mode === 'vertical'
        circlePosition = new paper.Point(
          eventPoint.x,
          (initialPoint.y + finalPoint.y) / 2
        );
      }

      const circle = new paper.Path.Circle(circlePosition, circleRadius);

      return { path, circle, circlePosition };
    },
    /* eslint-disable */
    addDelta(delta) {
      // Add the new delta
      this.xDeltas.push(delta.x);
      this.yDeltas.push(delta.y);

      if (this.xDeltas.length > 10) this.xDeltas.shift();
      if (this.yDeltas.length > 10) this.yDeltas.shift();
    },
    getAverageDelta() {
      // Calculate the average for x and y deltas
      const avgX =
        this.xDeltas.reduce((a, b) => a + b, 0) / this.xDeltas.length;
      const avgY =
        this.yDeltas.reduce((a, b) => a + b, 0) / this.yDeltas.length;

      return { x: avgX, y: avgY };
    },
    determineMode() {
      const { x, y } = this.getAverageDelta();
      if (Math.abs(x) > Math.abs(y)) {
        return 'vertical';
      } else {
        return 'horizontal';
      }
    },
    taskCompletedError() {
      this.$buefy.toast.open({
        duration: 3000,
        message: this.$t('task_completed_message'),
        type: 'is-warning',
      });
    },
    initializeLinearLengthTool() {
      this.linearLengthTool = new paper.Tool();
      this.linearLengthTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }

        if (this.lengthStage === 0) {
          if (this.isSelectingMarker(event)) {
            this.delegateToSelectTool(event);
          } else {
            if (this.processingNewBubble) {
              this.showProcessingBubblePopup();
              return;
            }
            // Make line
            const path = new paper.Path(event.point, event.point);
            path.strokeColor = '#000';
            path.strokeWidth = 2;
            // Make circle
            const circleRadius = this.squareSizeBubble / 2;
            const circle = new paper.Path.Circle(event.point, circleRadius);
            circle.strokeWidth = 0;
            circle.fillColor = '#000';
            // Make Label
            const label = new paper.PointText();
            label.fontStyle = 'normal';
            label.fillColor = 'white';
            label.content = this.getNextInspectionNumber();
            label.fontFamily = 'Siemens Roman';
            label.fontSize = this.fontSizeBubble;
            label.position = event.point;
            // label.fontWeight = 'bold';
            // Group all together
            const marker = new paper.Group(path, circle, label);
            const { isTableModeActive } = this;
            this.selectedItem = {
              id: uuidv4(),
              type: 'length',
              number: this.getNextInspectionNumber(),
              unit: this.user.unit === 'inch' ? 'in' : this.user.unit,
              expectedValue: '0.0000',
              decimalPlaces: 4,
              description: '',
              toleranceType: 'normal',
              positiveTolerance: '0.0000',
              negativeTolerance: '0.0000',
              measurementCount: 1,
              measurements: !isTableModeActive ? [EMPTY_MEASUREMENT_VALUE] : [],
              multipleMeasurements: false,
              marker,
              inTable: isTableModeActive,
              tableMeasurements: [[EMPTY_MEASUREMENT_VALUE]],
            };

            // Copy the data from the last marker of the same type
            const lengthInspections = this.inspections.filter(
              (inspection) => inspection.type === 'length'
            );
            if (lengthInspections.length > 0) {
              const lastLengthInspection =
                lengthInspections[lengthInspections.length - 1];
              this.copyMarkerData(lastLengthInspection, this.selectedItem);
            }

            // Move to stage 1
            this.lengthStage = 1;
          }
        } else if (this.lengthStage === 1) {
          const initialPoint =
            this.selectedItem.marker.children[0].segments[0].point;
          const finalPoint =
            this.selectedItem.marker.children[0].segments[1].point;

          // Make line
          const path = new paper.Path(
            initialPoint,
            event.point,
            event.point,
            finalPoint
          );
          path.strokeColor = '#000';
          path.strokeWidth = 2;
          // Make circle
          const circleRadius = this.squareSizeBubble / 2;
          const circleX = (initialPoint.x + finalPoint.x) / 2;
          const circleY = (initialPoint.y + finalPoint.y) / 2;
          const circlePosition = { x: circleX, y: circleY };
          const circle = new paper.Path.Circle(circlePosition, circleRadius);
          circle.strokeWidth = 0;
          circle.fillColor = '#000';
          // Make label
          const label = new paper.PointText();
          label.fillColor = 'white';
          label.content = this.getNextInspectionNumber();
          label.fontFamily = 'Siemens Roman';
          label.fontSize = this.fontSizeBubble;
          label.position = circlePosition;
          // label.fontWeight = 'bold';
          label.fontStyle = 'normal';

          // Remove previous marker and create new one
          this.selectedItem.marker.remove();
          this.selectedItem.marker = new paper.Group(path, circle, label);

          // Move to stage 2
          this.lengthStage = 2;
        } else if (this.lengthStage === 2) {
          const markerPosition = this.selectedItem.marker.children[1].position;

          this.selectedItem.firstPoint = this.getImagePoint(
            this.selectedItem.marker.children[0].segments[0].point
          );
          this.selectedItem.secondPoint = this.getImagePoint(
            this.selectedItem.marker.children[0].segments[1].point
          );
          this.selectedItem.thirdPoint = this.getImagePoint(
            this.selectedItem.marker.children[0].segments[2].point
          );
          this.selectedItem.fourthPoint = this.getImagePoint(
            this.selectedItem.marker.children[0].segments[3].point
          );
          this.selectedItem.point = this.getImagePoint(markerPosition);

          this.addNewInspection();

          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          this.showInspectionDetail(windowPoint);

          // Move to stage 0
          this.lengthStage = 0;
          this.addToolTipEvent(this.selectedItem, this.selectedItem.marker);
        }
      };
      this.linearLengthTool.onMouseMove = (event) => {
        if (this.lengthStage === 1) {
          const initialPoint =
            this.selectedItem.marker.children[0].segments[0].point;
          const finalPoint = event.point;

          // Make line
          const path = new paper.Path(initialPoint, finalPoint);
          path.strokeColor = '#000';
          path.strokeWidth = 2;
          // Make circle
          const circleRadius = this.squareSizeBubble / 2;
          const circleX = (initialPoint.x + finalPoint.x) / 2;
          const circleY = (initialPoint.y + finalPoint.y) / 2;
          const circlePosition = { x: circleX, y: circleY };
          const circle = new paper.Path.Circle(circlePosition, circleRadius);
          circle.strokeWidth = 0;
          circle.fillColor = '#000';
          // Make label
          const label = new paper.PointText();
          label.fillColor = 'white';
          label.content = this.getNextInspectionNumber();
          label.fontFamily = 'Siemens Roman';
          label.fontSize = this.fontSizeBubble;
          label.position = circlePosition;
          // label.fontWeight = 'bold';
          label.fontStyle = 'normal';

          // Remove previous marker and create new one
          this.selectedItem.marker.remove();
          this.selectedItem.marker = new paper.Group(path, circle, label);
        } else if (this.lengthStage === 2) {
          const initialPoint =
            this.selectedItem.marker.children[0].segments[0].point;
          const finalPoint =
            this.selectedItem.marker.children[0].segments[3].point;

          const dx = Math.abs(event.lastPoint.x - event.point.x);
          const dy = Math.abs(event.lastPoint.y - event.point.y);
          this.addDelta(event.delta);
          const mode = this.determineMode(event);
          const middlePoints = this.calculateMiddlePoints(
            mode,
            initialPoint,
            finalPoint,
            event.point
          );
          const { path, circle, circlePosition } = this.createPathAndCircle(
            initialPoint,
            finalPoint,
            middlePoints,
            event.point,
            mode
          );

          // Set path properties
          path.strokeColor = '#000';
          path.strokeWidth = 2;

          // Set circle properties
          circle.strokeWidth = 0;
          circle.fillColor = '#000';

          // Make label
          const fontSize = this.fontSizeBubble;
          const label = new paper.PointText({
            fillColor: 'white',
            content: this.getNextInspectionNumber(),
            fontFamily: 'Siemens Roman',
            fontSize,
            position: circlePosition,
            fontWeight: 'bold',
            fontStyle: 'normal',
          });

          // Update length mode based on current mode
          this.lengthMode = mode === 'horizontal' ? 0 : 1;

          // Remove previous marker and create new one
          this.selectedItem.marker.remove();
          this.selectedItem.marker = new paper.Group(path, circle, label);
        }
      };
    },
    calculateParallelPoints(initialPoint, finalPoint, mousePoint) {
      // Calculate unit direction vector for the line
      const lineDirection = finalPoint.subtract(initialPoint).normalize();

      // Calculate perpendicular vector to the line
      const perpendicular = new paper.Point(lineDirection.y, -lineDirection.x);

      // Determine the side of the mouse relative to the line
      const vectorToMouse = mousePoint.subtract(initialPoint);

      // Calculate distance along the perpendicular
      // This calculation considers both the mouse's distance to the line and its side
      const distanceToLine = vectorToMouse.dot(perpendicular);
      const distance = distanceToLine;

      // Calculate the middle points offset by the distance along the perpendicular
      const middlePoint1 = initialPoint.add(perpendicular.multiply(distance));
      const middlePoint2 = finalPoint.add(perpendicular.multiply(distance));

      return { middlePoint1, middlePoint2 };
    },

    initializeAlignedLengthTool() {
      this.alignedLengthTool = new paper.Tool();
      this.alignedLengthTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }

        if (this.lengthStage === 0) {
          if (this.isSelectingMarker(event)) {
            this.delegateToSelectTool(event);
          } else {
            if (this.processingNewBubble) {
              this.showProcessingBubblePopup();
              return;
            }
            // Make line
            const path = new paper.Path(event.point, event.point);
            path.strokeColor = '#000';
            path.strokeWidth = 2;
            // Make circle
            const circleRadius = this.squareSizeBubble / 2;
            const circle = new paper.Path.Circle(event.point, circleRadius);
            circle.strokeWidth = 0;
            circle.fillColor = '#000';
            // Make Label
            const label = new paper.PointText();
            label.fillColor = 'white';
            label.content = this.getNextInspectionNumber();
            label.fontFamily = 'Siemens Roman';
            label.fontSize = this.fontSizeBubble;
            label.position = event.point;
            // label.fontWeight = 'bold';
            label.fontStyle = 'normal';

            // Group all together
            const marker = new paper.Group(path, circle, label);
            const { isTableModeActive } = this;
            this.selectedItem = {
              id: uuidv4(),
              type: 'length',
              lengthType: 'aligned',
              number: this.getNextInspectionNumber(),
              unit: this.user.unit === 'inch' ? 'in' : this.user.unit,
              expectedValue: '0.0000',
              decimalPlaces: 4,
              description: '',
              toleranceType: 'normal',
              positiveTolerance: '0.0000',
              negativeTolerance: '0.0000',
              measurementCount: 1,
              measurements: !isTableModeActive ? [EMPTY_MEASUREMENT_VALUE] : [],
              multipleMeasurements: false,
              marker,
              inTable: isTableModeActive,
              tableMeasurements: [[EMPTY_MEASUREMENT_VALUE]],
            };

            // Copy the data from the last marker of the same type
            const lengthInspections = this.inspections.filter(
              (inspection) => inspection.type === 'length'
            );
            if (lengthInspections.length > 0) {
              const lastLengthInspection =
                lengthInspections[lengthInspections.length - 1];
              this.copyMarkerData(lastLengthInspection, this.selectedItem);
            }

            // Move to stage 1
            this.lengthStage = 1;
          }
        } else if (this.lengthStage === 1) {
          const initialPoint =
            this.selectedItem.marker.children[0].segments[0].point;
          const finalPoint =
            this.selectedItem.marker.children[0].segments[1].point;

          // Make line
          const path = new paper.Path(
            initialPoint,
            event.point,
            event.point,
            finalPoint
          );
          path.strokeColor = '#000';
          path.strokeWidth = 2;
          // Make circle
          const circleRadius = this.squareSizeBubble / 2;
          const circleX = (initialPoint.x + finalPoint.x) / 2;
          const circleY = (initialPoint.y + finalPoint.y) / 2;
          const circlePosition = { x: circleX, y: circleY };
          const circle = new paper.Path.Circle(circlePosition, circleRadius);
          circle.strokeWidth = 0;
          circle.fillColor = '#000';
          // Make label
          const label = new paper.PointText();
          label.fillColor = 'white';
          label.content = this.getNextInspectionNumber();
          label.fontFamily = 'Siemens Roman';
          label.fontSize = this.fontSizeBubble;
          label.position = circlePosition;
          // label.fontWeight = 'bold';
          label.fontStyle = 'normal';

          // Remove previous marker and create new one
          this.selectedItem.marker.remove();
          this.selectedItem.marker = new paper.Group(path, circle, label);

          // Move to stage 2
          this.lengthStage = 2;
        } else if (this.lengthStage === 2) {
          const markerPosition = this.selectedItem.marker.children[1].position;

          this.selectedItem.firstPoint = this.getImagePoint(
            this.selectedItem.marker.children[0].segments[0].point
          );
          this.selectedItem.secondPoint = this.getImagePoint(
            this.selectedItem.marker.children[0].segments[1].point
          );
          this.selectedItem.thirdPoint = this.getImagePoint(
            this.selectedItem.marker.children[0].segments[2].point
          );
          this.selectedItem.fourthPoint = this.getImagePoint(
            this.selectedItem.marker.children[0].segments[3].point
          );
          this.selectedItem.point = this.getImagePoint(markerPosition);

          this.addNewInspection();

          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          this.showInspectionDetail(windowPoint);

          // Move to stage 0
          this.lengthStage = 0;

          this.addToolTipEvent(this.selectedItem, this.selectedItem.marker);
        }
      };
      this.alignedLengthTool.onMouseMove = (event) => {
        if (this.lengthStage === 1) {
          const initialPoint =
            this.selectedItem.marker.children[0].segments[0].point;
          const finalPoint = event.point;

          // Make line
          const path = new paper.Path(initialPoint, finalPoint);
          path.strokeColor = '#000';
          path.strokeWidth = 2;
          // Make circle
          const circleRadius = this.squareSizeBubble / 2;
          const circleX = (initialPoint.x + finalPoint.x) / 2;
          const circleY = (initialPoint.y + finalPoint.y) / 2;
          const circlePosition = { x: circleX, y: circleY };
          const circle = new paper.Path.Circle(circlePosition, circleRadius);
          circle.strokeWidth = 0;
          circle.fillColor = '#000';
          // Make label
          const label = new paper.PointText();
          label.fillColor = 'white';
          label.content = this.getNextInspectionNumber();
          label.fontFamily = 'Siemens Roman';
          label.fontSize = this.fontSizeBubble;
          label.position = circlePosition;
          // label.fontWeight = 'bold';
          label.fontStyle = 'normal';

          // Remove previous marker and create new one
          this.selectedItem.marker.remove();
          this.selectedItem.marker = new paper.Group(path, circle, label);
        } else if (this.lengthStage === 2) {
          const initialPoint =
            this.selectedItem.marker.children[0].segments[0].point;
          const finalPoint =
            this.selectedItem.marker.children[0].segments[3].point;

          // Make line and circle
          const circleRadius = this.squareSizeBubble / 2;

          const points = this.calculateParallelPoints(
            initialPoint,
            finalPoint,
            event.point
          );

          const path = new paper.Path(
            initialPoint,
            points.middlePoint1,
            points.middlePoint2,
            finalPoint
          );

          // Convert middle points to Paper.js Point objects for easy calculations
          const p1 = new paper.Point(
            points.middlePoint1.x,
            points.middlePoint1.y
          );
          const p2 = new paper.Point(
            points.middlePoint2.x,
            points.middlePoint2.y
          );
          const mousePoint = event.point;

          // Calculate vectors
          const lineVector = p2.subtract(p1);
          const mouseVector = mousePoint.subtract(p1);

          // Project mouseVector onto lineVector
          let projection = lineVector
            .normalize()
            .multiply(mouseVector.dot(lineVector.normalize()));

          // Check if projection exceeds the line segment
          if (projection.length > lineVector.length) {
            projection = lineVector; // Snap to p2 if beyond
          } else if (projection.dot(lineVector) < 0) {
            projection = new paper.Point(0, 0); // Snap to p1 if before
          }

          // Calculate new circle position
          const circlePosition = p1.add(projection);
          const circle = new paper.Path.Circle(circlePosition, circleRadius);
          this.lengthMode = 0;
          path.strokeColor = '#000';
          path.strokeWidth = 2;
          circle.strokeWidth = 0;
          circle.fillColor = '#000';
          // Make label
          const label = new paper.PointText();
          label.fillColor = 'white';
          label.content = this.getNextInspectionNumber();
          label.fontFamily = 'Siemens Roman';
          label.fontSize = this.fontSizeBubble;
          label.position = circlePosition;
          // label.fontWeight = 'bold';
          label.fontStyle = 'normal';

          // Remove previous marker and create new one
          this.selectedItem.marker.remove();
          this.selectedItem.marker = new paper.Group(path, circle, label);
        }
      };
    },
    initializeVisualTool() {
      this.visualTool = new paper.Tool();
      this.visualTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }

        if (this.isSelectingMarker(event)) {
          this.delegateToSelectTool(event);
        } else {
          if (this.processingNewBubble) {
            this.showProcessingBubblePopup();
            return;
          }

          let measurements = new Array(4)
            .fill(null)
            .map(() =>
              JSON.parse(JSON.stringify(EMPTY_VISUAL_MEASUREMENT_VALUE))
            );

          const { isTableModeActive } = this;

          this.selectedItem = {
            id: uuidv4(),
            type: 'visual',
            number: this.getNextInspectionNumber(),
            orientation: 'up',
            description: '',
            measurementCount: 1,
            measurements: measurements,
            point: this.getImagePoint(event.point),
            inTable: isTableModeActive,
            tableMeasurements: [measurements.map((m) => ({ ...m }))],
          };

          // Copy the data from the last marker of the same type
          const visualInspections = this.inspections.filter(
            (inspection) => inspection.type === 'visual'
          );
          if (visualInspections.length > 0) {
            const lastVisualInspection =
              visualInspections[visualInspections.length - 1];
            this.copyMarkerData(lastVisualInspection, this.selectedItem);
          }

          this.addNewInspection();
          this.makeSimpleMarker(this.selectedItem);

          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          this.showInspectionDetail(windowPoint);
        }
      };
    },
    initializeGenericTool() {
      this.genericTool = new paper.Tool();
      this.genericTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }

        if (this.isSelectingMarker(event)) {
          this.delegateToSelectTool(event);
        } else {
          if (this.processingNewBubble) {
            this.showProcessingBubblePopup();
            return;
          }
          const { isTableModeActive } = this;
          this.selectedItem = {
            id: uuidv4(),
            type: 'generic',
            number: this.getNextInspectionNumber(),
            unit: this.user.unit === 'inch' ? 'in' : this.user.unit,
            tolerance: '0.0000',
            expectedValue: undefined,
            positiveTolerance: undefined,
            negativeTolerance: undefined,
            toleranceType: 'normal',
            decimalPlaces: 4,
            description: '',
            direction: '0',
            measurementCount: 1,
            orientation: 'up',
            measurements: !isTableModeActive ? [EMPTY_MEASUREMENT_VALUE] : [],
            multipleMeasurements: false,
            point: this.getImagePoint(event.point),
            inTable: isTableModeActive,
            tableMeasurements: [[EMPTY_MEASUREMENT_VALUE]],
          };

          // Copy the data from the last marker of the same type
          const genericInspections = this.inspections.filter(
            (inspection) => inspection.type === 'generic'
          );
          if (genericInspections.length > 0) {
            const lastGenericInspection =
              genericInspections[genericInspections.length - 1];
            this.copyMarkerData(lastGenericInspection, this.selectedItem);
          }

          this.addNewInspection();

          this.makeSimpleMarker(this.selectedItem);

          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          this.showInspectionDetail(windowPoint);
        }
      };
    },

    initializeFreefindingTool() {
      this.freeFindingTool = new paper.Tool();
      this.freeFindingTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }

        if (this.processingNewBubble) {
          this.$buefy.snackbar.open({
            duration: 5000,
            message: 'Hold on! The system is handling the last operation.',
            pauseOnHover: true,
            type: 'is-link',
            queue: false,
          });
          return;
        }

        if (this.isSelectingMarker(event)) {
          this.delegateToSelectTool(event);
        } else {
          if (this.processingNewBubble) {
            this.showProcessingBubblePopup();
            return;
          }
          this.selectedItem = {
            id: uuidv4(),
            type: 'freefinding',
            number: this.getNextInspectionNumber(),
            direction: '0',
            measurementCount: 1,
            description: '',
            orientation: 'up',
            measurements: [
              {
                value: '',
                images: [],
                comment: '',
              },
            ],
            multipleMeasurements: false,
            point: this.getImagePoint(event.point),
          };

          this.addNewInspection();

          this.makeSimpleMarker(this.selectedItem);

          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          this.showInspectionDetail(windowPoint);
        }
      };
    },

    // TODO: This is a duplicate of the method in the RAT component should be a MIXIN
    // after we merge the new RAT tasks into the main branch
    createRATBlankRow() {
      return {
        id: uuidv4(),
        level: 1,
        assembly: '',
        itemNumber: '',
        partName: '',
        partNumber: '',
        quantity: 1,
        reuse: 0,
        repair: 0,
        replace: 0,
        missing: 0,
        clientReject: false,
        received: 0,
        reviewed: false,
        qualityControlOverride: false,
        visual: '',
        dim: '',
        ndt: '',
        summary: '',
        repairScope: '',
        estimatedLabor: '',
      };
    },

    async addRATRow(isMinor) {
      const newRow = this.createRATBlankRow();
      if (isMinor) newRow.isMinor = isMinor;
      const job = this.$store.state.JobsModule.currentJob;
      const result = await this.$store.dispatch(ADD_REPAIR_ASSESSMENT_ROW, {
        job: job,
        rows: newRow,
        tool: this.tool,
      });

      return result.id;
    },

    initializeRATTool() {
      this.ratTool = new paper.Tool();
      this.ratTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }

        this.isItemDetailVisible = false;
        if (this.isSelectingMarker(event)) {
          this.delegateToSelectTool(event);
        } else {
          if (this.processingNewBubble) {
            this.showProcessingBubblePopup();
            return;
          }
          this.selectedItem = {
            type: 'rat',
            orientation: '0',
            point: this.getImagePoint(event.point),
            orientation: 'up',
          };

          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          await this.addNewInspection();
          await this.showInspectionRAT(windowPoint);
          this.isLoadingRatBubble = false;
          this.makeSimpleMarker(this.selectedItem);
        }
      };
    },
    initializeTextTool() {
      this.textTool = new paper.Tool();
      this.textTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }
        if (this.isSelectingMarker(event)) {
          this.delegateToSelectTool(event);
        } else {
          if (this.processingNewBubble) {
            this.showProcessingBubblePopup();
            return;
          }
          await this.closeInspection();
          this.selectedItem = {
            id: uuidv4(),
            type: 'text',
            text: 'Example Text',
            textSize: 18,
            orientation: 'up',
            point: this.getImagePoint(event.point),
          };

          // Copy the data from the last marker of the same type
          const textAnnotations = this.inspections.filter(
            (inspection) => inspection.type === 'text'
          );
          if (textAnnotations.length > 0) {
            const lastTextAnnotation =
              textAnnotations[textAnnotations.length - 1];
            this.copyMarkerData(lastTextAnnotation, this.selectedItem);
          }

          this.makeTextMarker(this.selectedItem);

          await this.addNewInspection();

          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          this.showInspectionDetail(windowPoint);
        }
      };
    },

    initializeLinkTool() {
      this.linkTool = new paper.Tool();
      this.linkTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }
        if (this.isSelectingMarker(event)) {
          this.delegateToSelectTool(event);
        } else {
          if (this.processingNewBubble) {
            this.showProcessingBubblePopup();
            return;
          }
          await this.closeInspection();
          this.selectedItem = {
            id: uuidv4(),
            type: 'link',
            text: 'Add link...',
            textSize: 22,
            orientation: 'up',
            linkedInternalDocument: null,
            point: this.getImagePoint(event.point),
          };

          this.makeLinkMarker(this.selectedItem);
          await this.addNewInspection();
          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          this.showInspectionDetail(windowPoint);
        }
      };
    },

    initializePositionImageTool() {
      this.positionImageTool = new paper.Tool();
      this.positionImageTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }

        const projectLayers = this.scope.project.layers;

        let hitResult = null;
        if (projectLayers.length > 1) {
          // Iterate through the children of the image layer
          projectLayers[1].children.forEach((item) => {
            const result = item.hitTest(event.point);
            if (result) {
              hitResult = result; // Save the hit result if found
            }
          });
        }

        if (hitResult) {
          this.delegateToSelectTool(event);
        } else {
          if (this.processingNewBubble) {
            this.showProcessingBubblePopup();
            return;
          }
          this.selectedItem = {
            id: uuidv4(),
            type: 'position-image',
            textSize: 150,
            orientation: 'up',
            point: this.getImagePoint(event.point),
            value: this.enablePositionImageType,
          };

          this.makePositionImageMarker(this.selectedItem);

          this.addNewInspection();

          const windowPoint = {
            x: event.event.clientX,
            y: event.event.clientY,
          };
          this.showInspectionDetail(windowPoint);
        }
      };
    },

    initializeArrowTool() {
      this.arrowTool = new paper.Tool();
      this.arrowTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }
        if (this.arrowStage === 0) {
          if (this.isSelectingMarker(event)) {
            this.delegateToSelectTool(event);
          } else {
            if (this.processingNewBubble) {
              this.showProcessingBubblePopup();
              return;
            }
            this.selectedItem = {
              id: uuidv4(),
              type: 'arrow',
              point: this.getImagePoint(event.point),
            };
            // Copy the data from the last marker of the same type
            const arrowAnnotations = this.inspections.filter(
              (inspection) => inspection.type === 'arrow'
            );
            if (arrowAnnotations.length > 0) {
              const lastArrowAnnotation =
                arrowAnnotations[arrowAnnotations.length - 1];
              this.copyMarkerData(lastArrowAnnotation, this.selectedItem);
            }

            const path = new paper.Path(event.point, event.point);
            const marker = new paper.Group(path);

            this.selectedItem.marker = marker;
            this.arrowStage = 1;

            this.arrowCircleMousePosition = {
              x: event.event.clientX,
              y: event.event.clientY,
            };
          }
        } else {
          const startPoint =
            this.selectedItem.marker.children[0].segments[0].point;
          let endPoint = event.point;
          const isInsideCanvas = this.scope.project.activeLayer.bounds.contains(
            event.point
          );
          if (!isInsideCanvas) {
            console.log({ isInsideCanvas });
            this.selectedItem.marker.remove();
            return; // Exit if the point is outside the canvas
          }
          if (this.shiftPressed && this.arrowEndPoint) {
            endPoint = this.arrowEndPoint;
          }

          this.selectedItem.startPoint = this.getImagePoint(startPoint);
          this.selectedItem.point = this.getImagePoint(endPoint);

          this.makeArrowMarker(this.selectedItem);
          this.addNewInspection();
          this.arrowStage = 0;
        }
      };
      this.arrowTool.onMouseMove = (event) => {
        if (this.arrowStage === 1) {
          const initialPoint =
            this.selectedItem.marker.children[0].segments[0].point;
          this.selectedItem.marker.remove();

          if (this.shiftPressed) {
            let endPoint = event.point;
            const vector = endPoint.subtract(initialPoint);
            const angle = vector.angle;
            const snappedAngle = Math.round(angle / 45) * 45;
            vector.angle = snappedAngle;
            endPoint = initialPoint.add(vector);
            this.arrowEndPoint = endPoint;
            const path = new paper.Path(initialPoint, endPoint);
            path.strokeColor = '#000';
            path.strokeWidth = 2;
            this.selectedItem.marker = new paper.Group(path);

            const snapHelper = document.getElementById(
              'arrow-circle-snap-helper'
            );
            if (snapHelper && this.arrowCircleMousePosition) {
              snapHelper.style.left = `${
                this.arrowCircleMousePosition.x - 50
              }px`;
              snapHelper.style.top = `${
                this.arrowCircleMousePosition.y - 150
              }px`;
            }
          } else {
            const path = new paper.Path(initialPoint, event.point);
            path.strokeColor = '#000';
            path.strokeWidth = 2;
            this.selectedItem.marker = new paper.Group(path);
          }
        }
      };
    },
    // start to initialize line
    initializeLineTool() {
      this.lineTool = new paper.Tool();
      this.lineTool.onMouseDown = async (event) => {
        if (this.readOnly || this.isTaskCompleted) {
          if (this.isTaskCompleted) this.taskCompletedError();
          return;
        }
        if (this.lineStage === 0) {
          if (this.isSelectingMarker(event)) {
            this.delegateToSelectTool(event);
          } else {
            if (this.processingNewBubble) {
              this.showProcessingBubblePopup();
              return;
            }
            // preparing instance of line after user selection of line
            this.selectedItem = {
              id: uuidv4(),
              type: 'line',
              point: this.getImagePoint(event.point),
            };

            // Copy the data from the last marker of the same type
            const lineAnnotations = this.inspections.filter(
              (inspection) => inspection.type === 'line'
            );
            if (lineAnnotations.length > 0) {
              const lastLineAnnotation =
                lineAnnotations[lineAnnotations.length - 1];
              this.copyMarkerData(lastLineAnnotation, this.selectedItem);
            }
            const path = new paper.Path(event.point, event.point);
            const marker = new paper.Group(path);

            this.selectedItem.marker = marker;
            this.lineStage = 1;

            this.arrowCircleMousePosition = {
              x: event.event.clientX,
              y: event.event.clientY,
            };
          }
        } else {
          const startPoint =
            this.selectedItem.marker.children[0].segments[0].point;
          let endPoint = event.point;
          if (this.shiftPressed && this.arrowEndPoint) {
            endPoint = this.arrowEndPoint;
          }

          this.selectedItem.startPoint = this.getImagePoint(startPoint);
          this.selectedItem.point = this.getImagePoint(endPoint);

          this.makeLineMarker(this.selectedItem);
          this.addNewInspection();
          this.lineStage = 0;
        }
      };
      this.lineTool.onMouseMove = (event) => {
        if (this.lineStage === 1) {
          const initialPoint =
            this.selectedItem.marker.children[0].segments[0].point;
          this.selectedItem.marker.remove();

          if (this.shiftPressed) {
            let endPoint = event.point;
            const vector = endPoint.subtract(initialPoint);
            const angle = vector.angle;
            const snappedAngle = Math.round(angle / 45) * 45;
            vector.angle = snappedAngle;
            endPoint = initialPoint.add(vector);
            this.arrowEndPoint = endPoint;
            const path = new paper.Path(initialPoint, endPoint);
            path.strokeColor = '#000';
            path.strokeWidth = 2;
            this.selectedItem.marker = new paper.Group(path);

            const snapHelper = document.getElementById(
              'arrow-circle-snap-helper'
            );
            if (snapHelper && this.arrowCircleMousePosition) {
              snapHelper.style.left = `${
                this.arrowCircleMousePosition.x - 50
              }px`;
              snapHelper.style.top = `${
                this.arrowCircleMousePosition.y - 150
              }px`;
            }
          } else {
            const path = new paper.Path(initialPoint, event.point);
            path.strokeColor = '#000';
            path.strokeWidth = 2;
            this.selectedItem.marker = new paper.Group(path);
          }
        }
      };
    },
    remakeMarker(inspection, selected = false, allBlack = false) {
      if (inspection.type === 'length') {
        this.makeLengthMarker(inspection, selected, allBlack);
      } else if (inspection.type === 'position-image') {
        this.makePositionImageMarker(inspection);
      } else if (inspection.type === 'text') {
        this.makeTextMarker(inspection);
      } else if (inspection.type === 'link' && !this.reportMode) {
        this.makeLinkMarker(inspection);
      } else if (inspection.type === 'arrow') {
        this.makeArrowMarker(inspection);
      } else if (inspection.type === 'line') {
        this.makeLineMarker(inspection);
      } else if (
        inspection.type !== 'text' &&
        inspection.type !== 'link' &&
        inspection.type !== 'line' &&
        inspection.type !== 'arrow'
      ) {
        this.makeSimpleMarker(inspection, selected, allBlack);
      }
    },
    makeSimpleMarker(inspection, markerSelected = false, allBlack = false) {
      const isInInspectorMode = this.isInInspectorMode();
      const rectangleSize = this.squareSizeBubble;
      const canvasPoint = this.getCanvasPoint(inspection.point);

      const rectangleCenter = {
        x: canvasPoint.x + rectangleSize / 2,
        y: canvasPoint.y + rectangleSize / 2,
      };

      const rectangleTopLeft = new paper.Point(canvasPoint.x, canvasPoint.y);
      const size = new paper.Size(this.squareSizeBubble, this.squareSizeBubble);
      const rectangle = new paper.Path.Rectangle(rectangleTopLeft, size);
      rectangle.strokeWidth = 0;

      // Make the triangle based on orientation
      const sideLength = rectangleSize;
      const triangleHeight = sideLength / 2;
      let bottomLeft, bottomRight, topVertex;

      if (inspection.orientation !== null) {
        if (inspection.orientation === 'up') {
          bottomLeft = new paper.Point(
            canvasPoint.x,
            canvasPoint.y + sideLength
          );
          bottomRight = new paper.Point(
            canvasPoint.x + sideLength,
            canvasPoint.y + sideLength
          );
          topVertex = new paper.Point(
            canvasPoint.x + sideLength / 2,
            canvasPoint.y + sideLength + triangleHeight
          );
        } else if (inspection.orientation === 'down') {
          bottomLeft = new paper.Point(canvasPoint.x, canvasPoint.y);
          bottomRight = new paper.Point(
            canvasPoint.x + sideLength,
            canvasPoint.y
          );
          topVertex = new paper.Point(
            canvasPoint.x + sideLength / 2,
            canvasPoint.y - triangleHeight
          );
        } else if (inspection.orientation === 'right') {
          bottomLeft = new paper.Point(canvasPoint.x, canvasPoint.y);
          bottomRight = new paper.Point(
            canvasPoint.x,
            canvasPoint.y + sideLength
          );
          topVertex = new paper.Point(
            canvasPoint.x - triangleHeight,
            canvasPoint.y + sideLength / 2
          );
        } else if (inspection.orientation === 'left') {
          bottomLeft = new paper.Point(
            canvasPoint.x + sideLength,
            canvasPoint.y
          );
          bottomRight = new paper.Point(
            canvasPoint.x + sideLength,
            canvasPoint.y + sideLength
          );
          topVertex = new paper.Point(
            canvasPoint.x + sideLength + triangleHeight,
            canvasPoint.y + sideLength / 2
          );
        }
      }

      // Create the triangle as a path
      const triangle = new paper.Path();
      triangle.add(bottomLeft);
      triangle.add(bottomRight);
      triangle.add(topVertex);
      triangle.closePath();
      triangle.closed = true;
      triangle.strokeWidth = 3;
      if (inspection.type === 'rat') {
        rectangle.fillColor = '#de9f00';
        triangle.fillColor = '#de9f00';
      } else if (inspection.type === 'freefinding') {
        rectangle.fillColor = '#0084E1';
        triangle.fillColor = '#0084E1';
      } else {
        if (markerSelected && isInInspectorMode) {
          rectangle.fillColor = '#15A1E1';
          triangle.fillColor = '#15A1E1';
        } else if (
          this.isCustomerReport ||
          this.hasNoMeasurements(inspection) ||
          allBlack
        ) {
          rectangle.fillColor = MAKERS_COLORS.default;
          triangle.fillColor = MAKERS_COLORS.default;
        } else if (isInInspectorMode && this.hasAllMeasurements(inspection)) {
          if (inspection.type === 'visual') {
            let notCompleted = inspection.measurements.every(
              (measurement) => measurement.value === 'notCompleted'
            );

            if (notCompleted) {
              rectangle.fillColor = MAKERS_COLORS.default;
              triangle.fillColor = MAKERS_COLORS.default;
            } else {
              if (this.areMeasurementsOk(inspection)) {
                rectangle.fillColor = '#66cc99';
                triangle.fillColor = '#66cc99';
              } else {
                rectangle.fillColor = '#E85960';
                triangle.fillColor = '#E85960';
              }
            }
          } else {
            if (this.areMeasurementsOk(inspection)) {
              rectangle.fillColor = '#66cc99';
              triangle.fillColor = '#66cc99';
            } else {
              rectangle.fillColor = '#E85960';
              triangle.fillColor = '#E85960';
            }
          }

          // TODO: check when type generic is ok or not
        } else if (inspection.type === 'generic') {
          if (this.areMeasurementsOk(inspection)) {
            rectangle.fillColor = '#66cc99';
            triangle.fillColor = '#66cc99';
          } else {
            if (inspection.inTable && inspection.measurements.length === 0) {
              rectangle.fillColor = MAKERS_COLORS.default;
              triangle.fillColor = MAKERS_COLORS.default;
            } else {
              rectangle.fillColor = '#E85960';
              triangle.fillColor = '#E85960';
            }
          }
        } else if (inspection.type === 'freefinding') {
          rectangle.fillColor = '#66cc99';
          triangle.fillColor = '#66cc99';
        } else {
          rectangle.fillColor =
            markerSelected && isInInspectorMode
              ? '#15A1E1'
              : MAKERS_COLORS.default;
          triangle.fillColor =
            markerSelected && isInInspectorMode
              ? '#15A1E1'
              : MAKERS_COLORS.default;
        }
      }

      // Make the label
      const label = new paper.PointText();
      label.fillColor = 'white';

      if (inspection.type === 'rat') {
        label.content = inspection.label ? inspection.label : 'RAT';
      } else {
        label.content = inspection.number;
      }

      label.fontFamily = 'Siemens Roman';
      label.fontSize = this.fontSizeBubble;
      label.position = rectangleCenter;
      // label.fontWeight = 'bold';
      label.fontStyle = 'normal';

      let marker;
      if (inspection.orientation) {
        marker = new paper.Group([triangle, rectangle, label]);
      } else {
        marker = new paper.Group([rectangle, label]);
      }

      if (inspection.orientation !== null) {
        if (inspection.orientation === 'up') {
          marker.position.x -= marker.bounds.width / 2;
          marker.position.y -= marker.bounds.height;
        } else if (inspection.orientation === 'down') {
          marker.position.x -= marker.bounds.width / 2;
          marker.position.y += triangleHeight;
        } else if (inspection.orientation === 'right') {
          marker.position.x += triangleHeight;
          marker.position.y -= marker.bounds.height / 2;
        } else if (inspection.orientation === 'left') {
          marker.position.x -= marker.bounds.width;
          marker.position.y -= marker.bounds.height / 2;
        } else {
          marker.position.x -= marker.bounds.width / 2;
          marker.position.y -= marker.bounds.height / 2;
        }
      }

      this.addToolTipEvent(inspection, marker);

      // If a marker already exists, remove it
      if (inspection.marker) {
        inspection.marker.remove();
      }

      inspection.marker = marker;
    },

    makePositionImageMarker(inspection) {
      const image = new paper.Raster({
        source: inspection.value,
        size: new paper.Size(inspection.textSize, inspection.textSize),
      });
      image.on('load', function () {
        image.size = new paper.Size(inspection.textSize, inspection.textSize);
        if (inspection.orientation) {
          if (inspection.orientation === 'down') {
            image.rotate(180, canvasPoint);
          } else if (inspection.orientation === 'left') {
            image.rotate(270, canvasPoint);
          } else if (inspection.orientation === 'right') {
            image.rotate(90, canvasPoint);
          }
        }
      });
      const canvasPoint = this.getCanvasPoint(inspection.point);
      image.position = canvasPoint;
      const projectLayers = this.scope.project.layers;
      if (projectLayers.length > 1) {
        projectLayers[1].insertChild(0, image);
      } else {
        const imageLayer = new paper.Layer();
        imageLayer.insertChild(0, image);
        this.scope.project.addLayer(imageLayer);
      }

      // If a marker already exists, remove it
      if (inspection.marker) {
        inspection.marker.remove();
      }
      inspection.marker = image;
    },

    makeTextMarker(inspection) {
      const label = new paper.PointText();
      label.fillColor = 'black';
      label.fontStyle = 'normal';
      label.content = inspection.text;
      label.fontFamily = 'Siemens Roman';
      label.fontSize = inspection.textSize;
      const canvasPoint = this.getCanvasPoint(inspection.point);
      label.position = canvasPoint;
      const marker = new paper.Group(label);

      if (inspection.orientation) {
        if (inspection.orientation === 'down') {
          marker.rotate(180, canvasPoint);
        } else if (inspection.orientation === 'left') {
          marker.rotate(270, canvasPoint);
        } else if (inspection.orientation === 'right') {
          marker.rotate(90, canvasPoint);
        }
      }
      // If a marker already exists, remove it
      if (inspection.marker) {
        inspection.marker.remove();
      }
      inspection.marker = marker;
    },

    makeLinkMarker(inspection) {
      const label = new paper.PointText();
      label.fillColor = '#0084E1';
      label.fontStyle = 'normal';
      label.content = '🔗 ' + inspection.text;
      label.fontFamily = 'Siemens Roman';
      label.fontSize = inspection.textSize;
      const canvasPoint = this.getCanvasPoint(inspection.point);
      label.position = canvasPoint;
      const marker = new paper.Group(label);

      if (inspection.orientation) {
        if (inspection.orientation === 'down') {
          marker.rotate(180, canvasPoint);
        } else if (inspection.orientation === 'left') {
          marker.rotate(270, canvasPoint);
        } else if (inspection.orientation === 'right') {
          marker.rotate(90, canvasPoint);
        }
      }
      // If a marker already exists, remove it
      if (inspection.marker) {
        inspection.marker.remove();
      }
      inspection.marker = marker;
    },

    async onOpenLinkFile(doc) {
      let path = doc.filePath;
      if (doc.type === 'file') {
        if (Array.from(path)[0] === '/') {
          path = path.substring(1);
        }
        const dijConfigurations = new DIJVueConfigurations();
        const response = await client.post(
          `/job/${doc.jobId}/reportfile-download-url`,
          { key: path, bucketName: dijConfigurations.bucket }
        );

        const anchor = document.createElement('a');
        anchor.style.display = 'none';
        anchor.href = response.data;
        anchor.target = '_blank';
        anchor.setAttribute('download', '');

        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }

      if (doc.type === 'url') {
        let url = (doc.filePath || '').trim();

        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url}`;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },

    rotateTextAnnotation(inspection) {
      const canvasPoint = this.getCanvasPoint(inspection.point);
      inspection.marker.rotate(90, canvasPoint);
    },

    makeArrowMarker(inspection) {
      const canvasStartPoint = this.getCanvasPoint(inspection.startPoint);
      const canvasEndPoint = this.getCanvasPoint(inspection.point);

      // Make sure the arrow tail is not too short by adding extra length
      if (
        Math.hypot(
          canvasEndPoint.x - canvasStartPoint.x,
          canvasEndPoint.y - canvasStartPoint.y
        ) < 15
      ) {
        // Move start point farther apart from end point if the two are too close.
        if (canvasEndPoint.x >= canvasStartPoint.x) {
          canvasStartPoint.x -= 15;
        } else if (canvasEndPoint.y >= canvasStartPoint.y) {
          canvasStartPoint.y -= 15;
        } else if (canvasEndPoint.x < canvasStartPoint.x) {
          canvasStartPoint.x += 15;
        } else if (canvasEndPoint.y < canvasStartPoint.y) {
          canvasStartPoint.y += 15;
        }
      }

      // Calculate arrowhead rotation angle
      const radian = Math.atan2(
        canvasEndPoint.y - canvasStartPoint.y,
        canvasEndPoint.x - canvasStartPoint.x
      );

      // Create the main line (the arrow body)
      const path = new paper.Path(canvasStartPoint, canvasEndPoint);
      path.strokeColor = '#000';
      path.strokeWidth = 2;

      // Arrowhead configuration
      const headLength = 15; // Length of the arrowhead lines
      const angleOffset = Math.PI / 8; // Angle between the arrowhead lines and the main line

      // Calculate the first line of the arrowhead
      const arrowheadLine1 = new paper.Point(
        canvasEndPoint.x - headLength * Math.cos(radian - angleOffset),
        canvasEndPoint.y - headLength * Math.sin(radian - angleOffset)
      );

      // Calculate the second line of the arrowhead
      const arrowheadLine2 = new paper.Point(
        canvasEndPoint.x - headLength * Math.cos(radian + angleOffset),
        canvasEndPoint.y - headLength * Math.sin(radian + angleOffset)
      );

      // Set stroke properties for the arrowhead lines
      arrowheadLine1.strokeColor = '#000';
      arrowheadLine1.strokeWidth = 2;

      arrowheadLine2.strokeColor = '#000';
      arrowheadLine2.strokeWidth = 2;

      const head = new paper.Path([
        canvasEndPoint,
        arrowheadLine1,
        arrowheadLine2,
      ]);
      head.closed = true;
      head.fillColor = '#000';

      // Group the path and arrowhead together
      if (inspection.marker) {
        inspection.marker.remove();
      }
      inspection.marker = new paper.Group([path, head]);
    },

    makeLineMarker(inspection, strokeWidth = 2) {
      const canvasStartPoint = this.getCanvasPoint(inspection.startPoint);
      const canvasEndPoint = this.getCanvasPoint(inspection.point);

      const path = new paper.Path(canvasStartPoint, canvasEndPoint);
      path.strokeColor = '#000';
      path.strokeWidth = strokeWidth;

      // Test for onHover to be more easy to select a line/arrow
      // path.onMouseEnter = (event) => {
      //   this.makeLineMarker(inspection, 10);
      // };

      // path.onMouseLeave = (event) => {
      //   this.makeLineMarker(inspection, 2);
      // };

      if (inspection.marker) {
        inspection.marker.remove();
      }
      inspection.marker = new paper.Group(path);
    },

    makeLengthMarker(inspection, markerSelected = false, allBlack = false) {
      const firstPoint = this.getCanvasPoint(inspection.firstPoint);
      const secondPoint = this.getCanvasPoint(inspection.secondPoint);
      const thirdPoint = this.getCanvasPoint(inspection.thirdPoint);
      const fourthPoint = this.getCanvasPoint(inspection.fourthPoint);
      const circlePosition = this.getCanvasPoint(inspection.point);
      let color;
      if (
        this.isCustomerReport ||
        this.hasNoMeasurements(inspection) ||
        allBlack
      ) {
        color = MAKERS_COLORS.default;
      } else if (this.areMeasurementsOk(inspection)) {
        color = '#66cc99';
      } else {
        color = '#E85960';
      }

      if (markerSelected) {
        color = '#15A1E1';
      }

      // Make path
      const path = new paper.Path(
        firstPoint,
        secondPoint,
        thirdPoint,
        fourthPoint
      );
      path.strokeColor = '#000';
      path.strokeWidth = 2;

      // Make circle
      const circleRadius = this.squareSizeBubble / 2;
      const circle = new paper.Path.Circle(circlePosition, circleRadius);
      circle.strokeWidth = 0;
      circle.fillColor = color;

      // Make label
      const label = new paper.PointText();
      label.fontStyle = 'normal';
      label.fillColor = 'white';
      label.content = inspection.number;
      label.fontFamily = 'Siemens Roman';
      label.fontSize = this.fontSizeBubble;
      label.position = circlePosition;
      // label.fontWeight = 'bold';

      // Remove previous marker and create new one
      if (inspection.marker) {
        inspection.marker.remove();
      }

      inspection.marker = new paper.Group(path, circle, label);
      this.addToolTipEvent(inspection, inspection.marker);
    },

    addToolTipEvent(inspection, marker) {
      marker.onMouseEnter = () => {
        if (
          this.isItemDetailVisible ||
          this.isItemRATVisible ||
          this.isMouseButtonDown
        )
          return;
        this.isItemToolTipVisible = true;
        this.toolTipInspection = inspection;

        this.$nextTick(() => {
          const modalElement = document.querySelector(
            '.dij-diagrambuilder-inspection-tooltip'
          );

          const container = document.getElementById(
            'canvas-diagram-' + this.task._id
          );

          if (modalElement && container) {
            const mouseX = window.event ? window.event.clientX : 0;
            const mouseY = window.event ? window.event.clientY : 0;

            // Default tooltip left positioning
            modalElement.style.left = `${mouseX + 20}px`;

            // Tooltip height
            const tooltipHeight = modalElement.offsetHeight;

            // Proposed top based on mouse position
            let proposedTop = mouseY - tooltipHeight / 2;

            // Get container bounds
            const containerRect = container.getBoundingClientRect();

            // Clamp top position within container bounds
            if (proposedTop < containerRect.top) {
              proposedTop = containerRect.top;
            } else if (proposedTop + tooltipHeight > containerRect.bottom) {
              proposedTop = containerRect.bottom - tooltipHeight;
            }

            modalElement.style.top = `${proposedTop}px`;
          }
        });
      };

      marker.onMouseLeave = () => {
        this.isItemToolTipVisible = false;
      };
    },
    moveImageTo(oldPoint, point, finished) {
      const dx = oldPoint.x - point.x;
      const dy = oldPoint.y - point.y;
      const newImageX = this.image.position.x - dx;
      const newImageY = this.image.position.y - dy;

      this.image.position = new paper.Point({ x: newImageX, y: newImageY });
      this.inspections.forEach((inspection) => {
        if (
          inspection.marker &&
          !['text', 'arrow', 'line', 'link'].includes(inspection.type)
        ) {
          const newMarkerX = inspection.marker.position.x - dx;
          const newMarkerY = inspection.marker.position.y - dy;
          inspection.marker.position = new paper.Point({
            x: newMarkerX,
            y: newMarkerY,
          });
        }
      });

      this.annotations.forEach((annotation) => {
        if (annotation.marker) {
          const newMarkerX = annotation.marker.position.x - dx;
          const newMarkerY = annotation.marker.position.y - dy;
          annotation.marker.position = new paper.Point({
            x: newMarkerX,
            y: newMarkerY,
          });
        }
      });

      if (this.multipleMeasurementSelector) {
        const newMarkerX = this.multipleMeasurementSelector.position.x - dx;
        const newMarkerY = this.multipleMeasurementSelector.position.y - dy;
        this.multipleMeasurementSelector.position = new paper.Point({
          x: newMarkerX,
          y: newMarkerY,
        });
      }

      if (this.isItemDetailVisible && finished) {
        this.showInspectionDetail(
          this.getImagePoint(this.selectedItem.marker.position)
        );
      }
    },
    isMobile() {
      if (
        typeof window.orientation !== 'undefined' ||
        navigator.userAgent.indexOf('IEMobile') !== -1
      ) {
        return true;
      }
      return false;
    },

    focusOnMarker(inspection) {
      // Disabled temporarly the panning until we found out a better solution
      this.selectedItem = inspection;

      return;
      const { marker } = inspection;

      const { canvas } = this.$refs;
      const targetX = canvas.width / 2;
      const targetY = canvas.height / 2;
      const dx = targetX - marker.position.x;
      const dy = targetY - marker.position.y;
      const targetNumberOfFrames = 80;
      const dxStep = dx / targetNumberOfFrames;
      const dyStep = dy / targetNumberOfFrames;

      let frameNumber = 0;

      const animation = () => {
        if (frameNumber !== 0) {
          const lastFrameNumber = frameNumber - 1;
          const lastPoint = new paper.Point(
            lastFrameNumber * dxStep,
            lastFrameNumber * dyStep
          );
          const point = new paper.Point(
            frameNumber * dxStep,
            frameNumber * dyStep
          );
          const finishedAnimation = frameNumber === 1;
          this.moveImageTo(lastPoint, point, finishedAnimation);
        }

        if (frameNumber++ < targetNumberOfFrames) {
          requestAnimationFrame(animation);
        }
      };

      if (!this.isMobile()) {
        animation();
      }
      if (this.isInInspectorMode()) {
        this.remakeMarker(inspection, true);
      }
      this.selectedItem = inspection;
    },
    blurFromMarker(inspection) {
      this.remakeMarker(inspection);
      if (this.multipleMeasurementSelector) {
        this.multipleMeasurementSelector.remove();
      }
    },
    getImagePoint(canvasPoint) {
      if (this.image) {
        const scaledImageX = canvasPoint.x - this.image.bounds.x;
        const scaledImageY = canvasPoint.y - this.image.bounds.y;

        const unscaledImageX =
          scaledImageX / this.image.getMatrix().a + this.image.getMatrix().b;
        const unscaledImageY =
          scaledImageY / this.image.getMatrix().d + this.image.getMatrix().c;

        return { x: unscaledImageX, y: unscaledImageY };
      }
      return { x: canvasPoint.x, y: canvasPoint.y };
    },
    getCanvasPoint(imagePoint) {
      if (!imagePoint) {
        return { x: 0, y: 0 };
      }

      if (this.image) {
        const canvasX =
          (imagePoint.x + this.image.getMatrix().b) * this.image.getMatrix().a +
          this.image.bounds.topLeft.x;
        const canvasY =
          (imagePoint.y + this.image.getMatrix().c) * this.image.getMatrix().d +
          this.image.bounds.topLeft.y;

        return { x: canvasX, y: canvasY };
      }
      return { x: imagePoint.x, y: imagePoint.y };
    },
    showInspectionDetail(point) {
      if (this.isTaskCompleted) {
        return;
      }

      let querySelectorString = '.dij-diagrambuilder-inspection-detail';

      if (this.selectedItem.type === 'rat') {
        this.isItemDetailVisible = false;
        this.isItemRATVisible = true;
        querySelectorString = '.dij-diagrambuilder-inspection-rat';
      } else if (
        !this.isInInspectorMode() ||
        this.selectedItem.type === 'freefinding'
      ) {
        this.isItemRATVisible = false;
        this.isItemDetailVisible = true;
        querySelectorString = '.dij-diagrambuilder-inspection-detail';
      }

      this.$nextTick(() => {
        const modalElement = document.querySelector(querySelectorString);
        const container = document.getElementById(
          'canvas-diagram-' + this.task._id
        ); // Ensure task is accessible via `this.task`

        if (modalElement && container) {
          const mouseX = point.x ? point.x : 0;
          const mouseY = point.y ? point.y : 0;

          modalElement.style.left = `${mouseX + 20}px`;

          const tooltipHeight = modalElement.offsetHeight;
          let proposedTop = mouseY - tooltipHeight / 2;

          const containerRect = container.getBoundingClientRect();

          // Clamp top within container
          if (proposedTop < containerRect.top) {
            proposedTop = containerRect.top;
          } else if (proposedTop + tooltipHeight > containerRect.bottom) {
            proposedTop = containerRect.bottom - tooltipHeight;
          }

          modalElement.style.top = `${proposedTop}px`;
        }
      });
    },

    async showInspectionRAT(point) {
      this.isItemRATVisible = true;
      this.$nextTick(() => {
        const modalElement = document.querySelector(
          '.dij-diagrambuilder-inspection-rat'
        );
        if (modalElement) {
          modalElement.style.left = `${point.x + 15}px`;
          modalElement.style.top = '100px';
        }
      });
    },

    showMultipleMeasurementSelector(inspection) {
      if (typeof inspection.selectedMeasurementNumber === 'undefined') {
        inspection.selectedMeasurementNumber = 0;
      }

      if (this.multipleMeasurementSelector) {
        this.multipleMeasurementSelector.remove();
      }

      if (inspection.type === 'visual' || inspection.inTable) return;

      this.multipleMeasurementSelector = new paper.Group();

      const noTolerance =
        inspection.type === 'generic' && !inspection.genericTolerance;

      const fillFor = (idx, selected) => {
        if (selected && inspection.type === 'freefinding') return 'lightblue';
        if (!this.hasMeasurement(inspection, idx)) return '#000';

        const hasValue = !!inspection.measurements[idx].value;
        if (noTolerance) return hasValue ? '#66cc99' : '#000';

        if (this.isMeasurementOk(inspection, idx)) return '#66cc99';
        return hasValue ? '#E85960' : '#000';
      };

      const makeCenteredLabel = (content, box) => {
        const label = new paper.PointText();
        label.content = content;
        label.fillColor = 'white';
        label.fontFamily = 'Siemens Roman';
        label.fontSize = this.fontSizeBubble;
        label.fontStyle = 'normal';
        label.justification = 'center';
        label.position = box.bounds.center;
        return label;
      };

      if (inspection.type === 'runout' && !inspection.multipleMeasurements) {
        // TIR
        let boxSize = new paper.Size(48, 50);
        const tirBox = new paper.Path.Rectangle(new paper.Point(0, 0), boxSize);

        if (inspection.selectedMeasurementNumber === 0) {
          tirBox.strokeColor = '#0084E1';
          tirBox.strokeWidth = 4;
        }
        tirBox.fillColor = fillFor(
          0,
          inspection.selectedMeasurementNumber === 0
        );

        const tirLabel = makeCenteredLabel('TIR', tirBox);

        const tirButton = new paper.Group(tirBox, tirLabel);
        tirButton.position = new paper.Point(0, 0);
        this.multipleMeasurementSelector.addChild(tirButton);

        // Position
        boxSize = new paper.Size(98, 50);
        const positionBox = new paper.Path.Rectangle(
          new paper.Point(0, 0),
          boxSize
        );
        if (inspection.selectedMeasurementNumber === 1) {
          positionBox.strokeColor = '#0084E1';
          positionBox.strokeWidth = 4;
        }
        positionBox.fillColor = fillFor(
          0,
          inspection.selectedMeasurementNumber === 1
        );

        const positionLabel = makeCenteredLabel('Position', positionBox);

        const positionButton = new paper.Group(positionBox, positionLabel);
        positionButton.position = new paper.Point(75, 0);
        this.multipleMeasurementSelector.addChild(positionButton);
      } else {
        // multi-point selector
        for (let i = 0; i < inspection.measurementCount; i++) {
          const boxSize = new paper.Size(48, 50);
          const box = new paper.Path.Rectangle(new paper.Point(0, 0), boxSize);

          if (inspection.selectedMeasurementNumber === i) {
            box.strokeColor = '#0084E1'; // or any highlight colour
            box.strokeWidth = 4; // thickness of the border
          }

          //  box.strokeWidth = 0;
          box.fillColor = fillFor(
            i,
            inspection.selectedMeasurementNumber === i
          );

          let content;
          if (inspection.type === 'runout') {
            content = inspection.multipleMeasurements
              ? Math.round(i * (360 / inspection.measurementCount))
              : i === 0
              ? 'TIR'
              : 'Pos';
          } else {
            content = i + 1;
          }

          const label = makeCenteredLabel(content, box);

          const button = new paper.Group(box, label);
          button.position = new paper.Point(i * 50, 0);
          this.multipleMeasurementSelector.addChild(button);
        }
      }

      const point = this.getCanvasPoint(inspection.point);
      const xPos =
        inspection.measurementCount > 1 ? inspection.measurementCount : 0;
      this.multipleMeasurementSelector.position = new paper.Point(
        point.x - xPos / 2,
        point.y - 80
      );
    },
    isInInspectorMode() {
      if (this.selectedItem) {
        if (this.selectedItem.type === 'rat') {
          return false;
        }
      }
      return this.tool === 'inspector';
    },
    hasNoMeasurements(inspection) {
      if (inspection.type === 'visual') {
        return !inspection.measurements.length > 0;
      }

      if (inspection.type === 'generic') {
        return (
          (!inspection.images || inspection.images.length === 0) &&
          inspection.measurements &&
          inspection.measurements.length !== 0 &&
          inspection.measurements[0].value === ''
        );
      }

      if (!inspection.measurements || inspection.measurements.length === 0) {
        return true;
      }

      for (let i = 0; i < inspection.measurements.length; i++) {
        const measurement = inspection.measurements[i];
        if (
          measurement &&
          measurement.value !== undefined &&
          measurement.value !== null &&
          !isNaN(measurement.value) &&
          measurement.value !== ''
        ) {
          return false;
        }
      }

      return true;
    },
    hasAllMeasurements(inspection) {
      if (inspection.type === 'visual') {
        return inspection.measurements.length > 0;
      }

      if (inspection.type === 'generic') {
        return inspection.images && inspection.images.length > 0;
      }

      if (!inspection.measurements || inspection.measurements.length === 0) {
        return false;
      }
      if (inspection.type === 'runout') {
        const measurement = inspection.measurements[0];
        if (
          !measurement ||
          measurement.value === undefined ||
          measurement.value === null ||
          isNaN(measurement.value) ||
          measurement.value === ''
        ) {
          return false;
        }
        return true;
      }
      for (let i = 0; i < inspection.measurements.length; i++) {
        const measurement = inspection.measurements[i];
        if (
          !measurement ||
          measurement.value === undefined ||
          measurement.value === null ||
          isNaN(measurement.value) ||
          measurement.value === ''
        ) {
          return false;
        }
      }
      return true;
    },
    hasMeasurement(inspection, measurementNumber) {
      const measurement = inspection.measurements[measurementNumber]?.value;
      if (measurement !== undefined && measurement !== null) {
        return true;
      }

      return false;
    },
    areMeasurementsOk(inspection) {
      let ok = true;
      if (!inspection) {
        console.warn('Inspection is undefined');
        return false;
      }
      if (
        inspection.type === 'generic' &&
        (!inspection.expectedValue || !inspection.toleranceType)
      ) {
        ok =
          (inspection.images && inspection.images.length > 0) ||
          (inspection.comment && inspection.comment.length) > 0 ||
          (inspection.measurements[0] &&
            inspection.measurements[0].value !== '');
      } else if (inspection.type === 'visual') {
        let notFilled = inspection.measurements.every(
          (measurement) => measurement.value === ''
        );

        ok = notFilled;
      } else {
        const limits = this.getLimits(inspection);
        const lowerLimit = limits[0];
        const upperLimit = limits[1];

        for (let i = 0; i < inspection.measurements.length; i++) {
          const measurement = parseFloat(inspection.measurements[i]?.value);
          ok = measurement >= lowerLimit && measurement <= upperLimit;
          if (!ok) {
            break;
          }
        }

        if (inspection.type === 'runout' && !inspection.multipleMeasurements) {
          const measurement = parseFloat(inspection.measurements[0]?.value);
          ok = measurement >= lowerLimit && measurement <= upperLimit;
        }
      }
      return ok;
    },
    isMeasurementOk(inspection, measurementNumber) {
      let ok = true;

      if (
        inspection.type === 'generic' &&
        (!inspection.expectedValue || !inspection.genericTolerance)
      ) {
        ok =
          (inspection.images && inspection.images.length > 0) ||
          (inspection.comment && inspection.comment.length) > 0;
      } else if (inspection.type === 'visual') {
        ok = inspection.measurements.length > 0;
      } else {
        const limits = this.getLimits(inspection);
        const lowerLimit = limits[0];
        const upperLimit = limits[1];

        const measurement = parseFloat(
          inspection.measurements[measurementNumber]?.value
        );
        if (measurement !== undefined && measurement !== null) {
          ok = measurement >= lowerLimit && measurement <= upperLimit;
        } else {
          ok = false;
        }
      }

      return ok;
    },
    getLimits(inspection) {
      let lowerLimit;
      let upperLimit;
      if (inspection.type === 'runout') {
        upperLimit = Math.abs(inspection.tolerance);
        lowerLimit = -upperLimit;
      } else {
        const floatExpectedValue = parseFloat(inspection.expectedValue);

        const floatNegativeTolerance =
          parseFloat(inspection.negativeTolerance) || 0;
        const floatPositiveTolerance =
          parseFloat(inspection.positiveTolerance) || 0;
        switch (inspection.toleranceType) {
          case 'normal':
            lowerLimit = floatExpectedValue - floatNegativeTolerance;
            upperLimit = floatExpectedValue + floatPositiveTolerance;

            break;
          case 'positive':
            upperLimit = Math.max(
              floatExpectedValue + floatNegativeTolerance,
              floatExpectedValue + floatPositiveTolerance
            );
            lowerLimit = Number.MIN_VALUE;
            break;
          case 'negative':
            lowerLimit = Math.min(
              floatExpectedValue + floatNegativeTolerance,
              floatExpectedValue + floatPositiveTolerance
            );
            upperLimit = Number.MAX_VALUE;
            break;
          default:
            throw new Error(
              `Failed to parse toleranceType for inspection with id ${inspection._id} and type ${inspection.type}: toleranceType ${inspection.toleranceType}`
            );
        }
      }

      let decimalPlaces = this.countDecimalPlaces(inspection.expectedValue);
      if (inspection.type === 'runout') {
        decimalPlaces = this.countDecimalPlaces(inspection.tolerance);
      }

      return [
        parseFloat(lowerLimit).toFixed(decimalPlaces),
        parseFloat(upperLimit).toFixed(decimalPlaces),
      ];
    },
    countDecimalPlaces(value) {
      if (!value) {
        return 0;
      }

      let numString = typeof value === 'number' ? value.toString() : value;

      let parts = numString.split('.');

      if (parts.length === 2) {
        return parts[1].length;
      } else {
        return 0;
      }
    },
    isOutsideMoveDeadzone(initialPoint, currentPoint) {
      return (
        Math.abs(initialPoint.x - currentPoint.x) > 10 ||
        Math.abs(initialPoint.y - currentPoint.y) > 10
      );
    },
    isNewTask() {
      return !this.task.diagram && !this.task.instruction;
    },
    isSelectingMarker(event) {
      const layers = this.scope.project.layers;
      const hitResult = this.scope.project.hitTest(event.point);
      if (hitResult && hitResult.type !== 'pixel') {
        return hitResult && hitResult.type !== 'pixel';
      }

      if (layers.length > 1) {
        // Perform the hitTest on items within the active layer only
        let hitResult = null;

        // Iterate through the children of the active layer
        layers[1].children.forEach((item) => {
          const result = item.hitTest(event.point);
          if (result) {
            hitResult = result; // Save the hit result if found
          }
        });
        return hitResult;
      }
    },
    async handleOnMouseDownMarkerSelection(event) {
      const hitResult = this.scope.project.hitTest(event.point);
      let item = null;
      if (hitResult.type === 'pixel') {
        item = hitResult.item;
      } else {
        item = hitResult.item.parent ? hitResult.item.parent : hitResult.item;
      }

      // If the user clicks the measurementSelector, update the point
      if (item.parent === this.multipleMeasurementSelector) {
        this.selectedItem.selectedMeasurementNumber =
          item.parent.children.indexOf(item);
      } else {
        // Find the selected item
        const allItems = this.inspections.concat(this.annotations);

        const foundItem = allItems.filter(
          (inspection) => inspection.marker === item
        )[0];

        if (foundItem) {
          this.isItemDetailVisible = false;
          if (foundItem.type === 'rat') {
            // await this.$store.dispatch(GET_JOB, this.$route.params.id);
            const job = this.$store.state.JobsModule.currentJob;

            let row = {};

            if (job.repairAssessment)
              row = job.repairAssessment.find(
                (rat) => rat.id === foundItem.ratRowID
              );

            // Assigning the RAT row into the selected item without braking the reactivity
            for (let key in row) {
              if (row.hasOwnProperty(key)) {
                if (key !== 'version') {
                  foundItem[key] = row[key];
                }
              }
            }

            this.selectedItem = foundItem;
          }
          if (foundItem.type === 'position-image') {
            this.selectedItem = foundItem;
          } else {
            this.isItemRATVisible = false;
            this.selectedItem = foundItem;
          }
          this.isMouseButtonDown = true;
        }
      }
    },
    delegateToSelectTool(event) {
      // Handle the onMouseDown marker selection and then enable the selectTool
      // to handle both onMouseDrag(if any) and onMouseUp
      this.handleOnMouseDownMarkerSelection(event);
      this.switchBackTool = this.scope.tool;
      this.selectTool.activate();
    },
    copyMarkerData(from, to) {
      switch (from.type) {
        case 'diameter':
        case 'length':
        case 'generic':
          to.unit = from.unit;
          to.expectedValue = from.expectedValue;
          to.toleranceType = from.toleranceType;
          to.positiveTolerance = from.positiveTolerance;
          to.negativeTolerance = from.negativeTolerance;
          to.orientation = from.orientation;
          to.measurementCount = from.measurementCount;
          to.multipleMeasurements = from.multipleMeasurements;
          to.measurements = [];
          if (from.inTable) {
            to.tableMeasurements = from.tableMeasurements;
          }
          for (let i = 0; i < to.measurementCount; i++) {
            to.measurements.push(EMPTY_MEASUREMENT_VALUE);
          }
          break;
        case 'runout':
          to.unit = from.unit;
          to.tolerance = from.tolerance;
          to.direction = from.direction;
          to.orientation = from.orientation;
          to.measurementCount = from.measurementCount;
          to.multipleMeasurements = from.multipleMeasurements;
          to.measurements = [];
          for (let i = 0; i < to.measurementCount; i++) {
            to.measurements.push(EMPTY_MEASUREMENT_VALUE);
          }
          if (from.inTable) {
            to.tableMeasurements = from.tableMeasurements;
          }
          break;
        case 'visual':
          to.orientation = from.orientation;
          to.measurementCount = 1;
          if (!this.isTableModeActive)
            to.measurements = [
              EMPTY_VISUAL_MEASUREMENT_VALUE,
              EMPTY_VISUAL_MEASUREMENT_VALUE,
              EMPTY_VISUAL_MEASUREMENT_VALUE,
              EMPTY_VISUAL_MEASUREMENT_VALUE,
            ];
          if (from.inTable) {
            to.tableMeasurements = from.tableMeasurements;
          }
          break;
        case 'arrow':
          break;
        case 'line':
          break;
        case 'link':
          break;
        case 'text':
          to.textSize = from.textSize;
          to.orientation = from.orientation;
          break;
        default:
          throw new Error(`Unknown Type: ${to.type}`);
      }
    },
    beforeSave() {
      this.task.subtype = 'diagram';
      this.inspections.forEach((inspection) => {
        delete inspection.marker;
        delete inspection.selectedMeasurementNumber;
      });

      this.annotations.forEach((inspection) => delete inspection.marker);

      this.task.diagram = {
        src: this.image.image.src,
        inspections: this.inspections,
        annotations: this.annotations,
      };
    },

    // Update all measurments when adding or deleting a new row in table measurments
    // also when changing the name of rows
    updateInTableMeasurments(tableMeasurment) {
      tableMeasurment.forEach((tableMeasurement) => {
        const inspectionIndex = this.inspections.findIndex(
          (insp) => insp._id === tableMeasurement._id
        );

        this.inspections[inspectionIndex].measurementCount =
          tableMeasurement.measurementCount;
        this.inspections[inspectionIndex].measurements =
          tableMeasurement.tableMeasurements;
      });

      this.$store.dispatch(SAVE_BUBBLES, {
        taskId: this.task._id,
        data: this.inspections,
      });
    },

    // Update one measurment on cell leavs
    async updateInTableMeasurment(tableMeasurment) {
      const inspectionIndex = this.inspections.findIndex(
        (insp) => insp._id === tableMeasurment._id
      );

      this.inspections[inspectionIndex].measurementCount =
        tableMeasurment.measurementCount;
      this.inspections[inspectionIndex].measurements =
        tableMeasurment.measurements;
      // this.$store.dispatch(SAVE_BUBBLES, {
      //   taskId: this.task._id,
      //   data: this.inspections,
      // });

      // UNCOMENT IN CASE OF ISSUES WITH THE UPDATING OF THE MEASURMENTS IN THE BUBBLES
      const isInspector = this.tool === 'inspector';
      // await this.handleBubbleUpdate({
      //   ...new BubbleDTO(this.inspections[inspectionIndex]),
      //   isInspector,
      // });
      // this.inspections.forEach((inspection) => {
      //   if (inspection.inTable) {
      //     this.remakeMarker(inspection, false, true);
      //   }
      // });
    },

    selectTableInspection(inspection) {
      this.$refs.list.selectInspection(inspection);
      this.selectedItem = inspection;
    },

    // Set the colors for the bubbles with measurments for that row in table measurments
    setColorsForBubblesRow(inspections, focusedInspection) {
      inspections.forEach((item) => {
        if (item._id === focusedInspection._id) {
          this.remakeMarker(item, true);
        } else {
          this.remakeMarker(item);
        }
      });
    },

    handleFileChooserChange(event) {
      // User clicked cancel
      if (event.target.files.length === 0) {
        return;
      }

      const file = event.target.files[0];
      const fileReader = new FileReader();
      fileReader.onload = () => {
        const image = new Image();
        image.onload = () => {
          this.imageToBeAnnotated = image;
          this.showAnnotator();
        };
        image.src = fileReader.result;
      };
      fileReader.readAsDataURL(file);
      this.filename = `${uuidv4()}.jpg`;

      // Clear the file list to allow the user to repeat the upload
      event.target.value = '';
    },
    showAnnotator() {
      this.isAnnotatorVisible = true;
    },
    hideAnnotator() {
      this.isAnnotatorVisible = false;
      this.imageToBeAnnotated = null;
    },
    showGallery() {
      if (this.selectedRatRow.images && this.selectedRatRow.images.length > 0) {
        this.isGalleryVisible = true;
      }
    },
    hideGallery() {
      this.isGalleryVisible = false;
    },
    showCamera() {
      this.isCameraVisible = true;
    },

    hideCamera() {
      this.isCameraVisible = false;
    },

    processCamera(image) {
      this.hideCamera();
      this.imageToBeAnnotated = image;
      this.showAnnotator();
      this.filename = `${uuidv4()}.jpg`;
    },

    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, {
        type: mime,
      });
    },

    async uploadToS3(file, path) {
      const dijConfigurations = new DIJVueConfigurations();
      const url = await dijConfigurations.uploadFileToS3(file, file.name, path);
      return url;
    },

    handleFileChooserChange(event) {
      // User clicked cancel
      if (event.target.files.length === 0) {
        return;
      }
      const showAnnotator = event.target.files.length === 1;
      if (showAnnotator) {
        const file = event.target.files[0];
        const fileReader = new FileReader();
        fileReader.onload = () => {
          const image = new Image();
          image.onload = () => {
            this.imageToBeAnnotated = image;
            this.showAnnotator();
          };
          image.src = fileReader.result;
        };
        fileReader.readAsDataURL(file);
        this.filename = `${uuidv4()}.jpg`;
      } else {
        const { files } = event.target;

        const promises = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const fileReader = new FileReader();

          fileReader.onerror = () => {
            fileReader.abort();
          };

          fileReader.onload = () => {
            const image = new Image();
            image.onload = () => {
              const canvas = document.createElement('canvas');
              imageHelper.drawImageFor1920px(canvas, image);
              const fileName = `${uuidv4()}.jpg`;

              const fileToBeSaved = this.dataURLtoFile(
                canvas.toDataURL('image/jpeg', 0.9),
                fileName
              );
              const workOrder = this.job.name;
              const site = this.user.location.substr(0, 3);
              const path = `uploads/sites/${site}/${workOrder}/ra/${this.selectedItem.ratRowID}`;
              const promise = this.uploadToS3(fileToBeSaved, path).then(
                (url) => {
                  this.selectedItem.images.push({
                    description: '',
                    name: '',
                    url,
                  });
                }
              );
              promises.push(promise);
            };

            image.src = fileReader.result;
          };
          fileReader.readAsDataURL(file);
        }
      }

      // Clear the file list to allow the user to repeat the upload
      event.target.value = '';
    },
    showAnnotator() {
      this.isAnnotatorVisible = true;

      document.querySelector('div.menu-wrapper').style.visibility = 'hidden';
    },
    hideAnnotator() {
      this.isAnnotatorVisible = false;
      this.imageToBeAnnotated = null;
      document.querySelector('div.menu-wrapper').style.visibility = 'visible';
    },
    showGallery() {
      if (this.selectedItem.images && this.selectedItem.images.length > 0) {
        this.isGalleryVisible = true;
        document.querySelector('div.menu-wrapper').style.visibility = 'hidden';
      }
    },
    hideGallery() {
      this.isGalleryVisible = false;
      document.querySelector('div.menu-wrapper').style.visibility = 'visible';
    },
    showCamera() {
      this.isCameraVisible = true;
    },

    hideCamera() {
      this.isCameraVisible = false;
    },

    processCamera(image) {
      this.hideCamera();
      this.imageToBeAnnotated = image;
      this.showAnnotator();
      this.filename = `${uuidv4()}.jpg`;
    },

    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, {
        type: mime,
      });
    },

    processAnnotator({ image, imageDesc }) {
      this.hideAnnotator();
      const fileName = `${this.filename.substr(
        0,
        this.filename.lastIndexOf('.')
      )}.jpg`;
      const canvas = document.createElement('canvas');
      imageHelper.drawImageFor1920px(canvas, image);
      const file = this.dataURLtoFile(
        canvas.toDataURL('image/jpeg', 0.9),
        fileName
      );
      const workOrder = this.job.name;
      const site = this.user.location.substr(0, 3);
      const imageId = uuidv4();
      const path = `uploads/sites/${site}/${workOrder}/ra/${imageId}`;
      this.uploadToS3(file, path).then((url) => {
        this.selectedItem.images.push({
          description: imageDesc.description,
          name: imageDesc.name,
          url,
        });
      });
    },

    async newAnnotator({ image, file, imageDesc }) {
      await this.processAnnotator({
        image,
        imageDesc,
      });
      if (this.annotatorCameraNext) {
        this.showCamera();
      } else {
        this.handleFileChange(file);
      }
    },

    async handleFileChange(file) {
      if (this.annotatorCameraNext) {
        this.annotatorCameraNext = false;
      }
      if (!file) {
        return;
      }
      this.imageOriginalName = file.name;

      const fileReader = new FileReader();
      fileReader.onload = () => {
        const image = new Image();
        image.onload = () => {
          this.imageToBeAnnotated = image;
          this.showAnnotator();
        };
        image.src = fileReader.result;
      };
      fileReader.readAsDataURL(file);
    },

    async uploadToS3(file, path) {
      const dijConfigurations = new DIJVueConfigurations();
      const url = await dijConfigurations.uploadFileToS3(file, file.name, path);
      return url;
    },
  },
};
</script>

<style lang="scss" scoped>
.dij-diagrambuilder {
  position: relative;
  height: 100%;
}

.dij-diagrambuilder-inspection-rat {
  position: fixed;
  border: 1px solid lightgray;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 80vh;

  .modal-card-head,
  .modal-card-foot {
    display: flex;
    justify-content: space-between;
    padding: 5px;
  }
}

.dij-diagrambuilder-inspection-tooltip {
  position: fixed;
  border: 1px solid lightgray;
  z-index: 1;
  display: flex;
  flex-direction: column;
  .modal-card-head,
  .modal-card-foot {
    display: flex;
    justify-content: space-between;
    padding: 5px;
  }
}

.dij-diagrambuilder-toolbar-rat {
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  color: #656565;
  &.disabled {
    opacity: 0.5;
    cursor: unset;
  }

  &.dij-tool-active {
    color: #3fa9f5;
  }
}

.dij-diagrambuilder-toolbar-freefinding {
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  color: #656565;
  &.disabled {
    opacity: 0.5;
    cursor: unset;
  }

  &.dij-tool-active {
    color: #3fa9f5;
  }
}

.dij-diagrambuilder-canvas-container {
  position: relative;
  height: 100%;
  // lower the text size for label and fields
  .field ::v-deep label,
  .control ::v-deep input,
  ::v-deep .control input {
    font-size: 75%;
  }
}
.dij-diagrambuilder-zoomoption {
  --zoom-control-size: 36px;
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 56px;
  height: fit-content;
  gap: 10px;
  box-shadow: 0 1px 2px rgb(0 0 0 / 40%);
  color: dimgray;
  padding: 10px;
  z-index: 1;
  background-color: white;

  span {
    cursor: pointer;

    &.disabled {
      opacity: 0.5;
      cursor: unset;
    }
  }
}

.dij-diagrambuilder-zoom-icon {
  width: var(--zoom-control-size);
  height: var(--zoom-control-size);
  display: flex;
  align-items: center;
  justify-content: center;

  ::v-deep .icon {
    width: var(--zoom-control-size);
    height: var(--zoom-control-size);
  }

  ::v-deep .mdi {
    font-size: 30px;
  }
}

.dij-diagrambuilder-zoom-image {
  width: var(--zoom-control-size);
  height: var(--zoom-control-size);
  cursor: pointer;

  &.disabled {
    opacity: 0.5;
    cursor: unset;
  }
}

.dij-diagrambuilder-canvas {
  position: absolute;
  height: 100%;
  canvas {
    // position: absolute;
    width: 100%;
    height: 100%;
    background-color: white;
  }
}

span.tag.is-info.is-rounded {
  position: relative;
  left: -21px;
  top: -7px;
  font-size: 10px;
}

.dij-diagrambuilder-upload {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  button {
    cursor: pointer;
  }
}
.dij-diagrambuilder-inspection-detail {
  position: fixed;
  border: 1px solid lightgray;
  z-index: 1;

  .modal-card-head,
  .modal-card-foot {
    display: flex;
    justify-content: space-between;
    padding: 5px;
  }
}
.dij-diagrambuild-job-info {
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 20px;
  font-size: 12px;
}

.bubble-zoom {
  // Needs to be calculated taking into accout the inspections
  right: 225px;
}

.bubble-zoom-at-margin {
  // Needs to be calculated taking into accout the inspections
  right: 0;
}

.dij-inspection-detail-body {
  max-height: calc(100vh - 185px);
}

.table-content {
  display: flex;
  height: 100%;
}

#arrow-circle-snap-helper {
  width: 100px;
  height: 100px;
  opacity: 0.2;
  border-radius: 50%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.arrow-line-snap {
  position: absolute;
  width: 2px;
  height: 50%;
  background: black;
  transform-origin: bottom;
  bottom: 50%;
}
</style>

<style lang="scss">
.dij-inspection-detail-body {
  .dropdown-menu {
    width: 160px !important;
    min-width: 160px !important;
  }
}

.modal-table-card {
  background: white;
  border-radius: 10px;
  padding: 30px 10px 10px 10px;
  margin: 0 25%;
  display: flex;
  flex-direction: column;
  height: 650px;
}

.modal-table-card-footer {
  display: flex;
  flex-direction: row;
  background: white;
  padding: 10px 20px;
  border-radius: 10px;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  justify-content: space-between;
}

.dij-table-measurments {
  position: absolute;
  bottom: 0;
  width: 100%;
}

div.dij-ra-annotator-modal > ::v-deep div.animation-content.modal-content {
  width: unset;
  height: unset;
  overflow: hidden;
}
</style>
